import { Controller } from "@hotwired/stimulus"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import { getCsrfToken } from "shared/form_helpers"
import { areaStore } from "../../stores/locales/area_store"
import { allDevicesStore } from "../../stores/devices/all_devices_store"
import { allDeviceTypesStore } from "../../stores/device_types/all_device_types_store"
import { allBundlesStore } from "../../stores/device_types/all_bundles_store"
import { allDeploymentStatusesStore } from "../../stores/deployment_statuses/all_deployment_statuses_store"
import { deviceTypeStore } from "../../stores/device_types/device_type_store"
import { bundleStore } from "../../stores/device_types/bundle_store"
import { useStore } from "stimulus-store"
import { isBundle } from "shared/device_types/device_type_id_helpers"
import { initializeTooltips, disposeTooltips, disposeTooltip } from "shared/tooltip"

// Connects to data-controller="floorplans--floor-plan-icon-manager"
export default class extends Controller {

    static stores = [
        areaStore,
        allBundlesStore,
        allDeploymentStatusesStore,
        allDevicesStore,
        allDeviceTypesStore,
        deviceTypeStore,
        bundleStore
    ]

    static targets = [
        'floorPlan',
        'deviceList',
        'iconContainer',
        'dropZone',
        'coordinateX',
        'coordinateY'
    ]

    static values = {
        iconSize: Number,
        defaultIcon: String,
        updateUrl: String,
        enableTooltips: { type: Boolean, default: true },
        enableDoubleClick: { type: Boolean, default: true },
        isUpdatePage: { type: Boolean, default: false },
        deviceTypeFilter: { type: String, default: '' },

        // Configurable behavior values (defaults reproduce current behavior)
        clickAction: { type: String, default: 'none' },
        holdAction: { type: String, default: 'none' },
        doubleClickAction: { type: String, default: 'edit-device' },
        drag: { type: Boolean, default: true },
        tooltipMode: { type: String, default: 'device-info' },
        holdDelay: { type: Number, default: 500 },
        iconColorMode: { type: String, default: 'deployment-status' },
        iconFixedColor: { type: String, default: '#0d6efd' }
    }

    connect() {
        // Mapping-status state (Visual Print Mapper). Initialize BEFORE useStore(this):
        // useStore synchronously fires the store callbacks (e.g. onAllDevicesStoreUpdate
        // -> renderIcons), and renderIcons()/getIconColor() read this.mappedDeviceIds and
        // this.hideMappedDevices — so they must already exist or the first render throws.
        // Defaults (empty set, hide off) are inert for every other page/color mode.
        this.mappedDeviceIds = new Set()
        this.hideMappedDevices = false

        // Coalesced-render state. A single area load fires several store callbacks in
        // sequence (onAllDevicesStoreUpdate, onAllBundlesStoreUpdate,
        // onAllDeploymentStatusesStoreUpdate, onAreaStoreUpdate) and the Visual Print
        // Mapper adds another render via setMappedDeviceIds(). Each previously did a full
        // teardown/rebuild of every icon, so the icon set was destroyed and recreated 5+
        // times per load. scheduleRender() batches all of these into a single
        // requestAnimationFrame so the DOM is rebuilt once per burst. This also removes
        // the race that left the "mapped" green color / connection line stale until a
        // page refresh: the recolor and the store-driven re-renders now collapse into one
        // deterministic pass instead of clobbering each other. Initialized BEFORE
        // useStore(this) because useStore fires store callbacks synchronously.
        this._renderRafId = null
        this._pendingClearDropped = false

        // Per-render lookup maps (id -> record), rebuilt once at the start of each
        // renderIcons() pass. They replace the previous per-device Array.prototype.find()
        // scans over the device-types / deployment-statuses / bundles stores (which were
        // O(devices x storeSize)) and the per-interaction linear scan in findDeviceById().
        this._deviceTypeById = null
        this._deploymentStatusById = null
        this._bundleById = null
        this._deviceById = null

        useStore(this)

        // Map legacy behavior values (enableDoubleClick/enableTooltips) onto the
        // new configurable behavior system before any listeners are set up.
        this.applyBackwardCompatibility()

        // Validate configured behavior actions and fall back to 'none' for any
        // unrecognized value so connect() never throws for any combination of inputs.
        this.resolveBehaviorConfig()

        // Icon rendering state
        this.ensureDeviceListPositioning()

        // Dragging state
        this.draggedElement = null
        this.dragOffset = { x: 0, y: 0 }
        this.updateTimeout = null
        this.isDragging = false
        this.pendingUpdate = null

        // Dropped icon state
        this.droppedIcon = null

        // Device visibility state - existing devices are shown by default
        this.showExistingDevices = true

        // Mobile detection
        this.isMobile = this.detectMobileDevice()

        // Touch handling for mobile
        this.touchTimer = null
        this.touchStartTime = null
        
        // Track if icon click listeners have been set up
        this.iconClickListenersSetup = false

        // Hold-gesture flag: set true when a hold action fires so the synthetic
        // click that follows the press is suppressed by handleIconClick.
        this.holdFired = false

        // Store bound handler references for proper listener management
        this.boundHandleIconClick = null
        this.boundHandleDeviceIconDoubleClick = null
        this.boundHandleDeviceIconTouchStart = null
        this.boundHandleDeviceIconTouchEnd = null
        this.boundHandleDeviceIconTouchMove = null

        // Hold-gesture (press-and-hold) detection state. Populated by
        // handleHoldStart and cleared by handleHoldEnd/handleHoldCancel. Only ever
        // active when holdActionValue !== 'none' (listeners are gated in
        // setupIconClickListeners). Exactly one hold timer is active at a time.
        this.holdTimer = null
        this.holdTarget = null
        this.holdStartPosition = null

        // Bound hold-detection handler references (mouse + touch) for proper cleanup.
        this.boundHandleHoldStartMouse = null
        this.boundHandleHoldEndMouse = null
        this.boundHandleHoldStartTouch = null
        this.boundHandleHoldEndTouch = null
        this.boundHandleHoldCancelTouch = null

        // Mapping-card (hold-to-show print mapping) state. The card is a plain,
        // absolutely-positioned <div class="floorplan-mapping-card"> appended into
        // the deviceList container (a sibling of the icons), NOT a Bootstrap popover
        // anchored to the icon element. This makes it independent of the icon's
        // lifecycle: renderIcons()/clearIcons() only remove elements matching
        // `div[data-icon-type]` / `[data-bs-toggle="tooltip"]`, and the card carries
        // neither attribute, so store-driven re-renders leave it untouched (it no
        // longer flashes away). Only one card is ever shown at a time.
        // `activeMappingCardElement` holds the live card <div> (or null when no card
        // is shown), `activeMappingCardAnchor` is the icon element that was held (used
        // for positioning + event dispatch), `activeMappingCardDeviceId` is the held
        // device/workstation id (used for delete requests), and the bound document
        // handler is used to dismiss the card on an outside click. All are cleared by
        // dismissMappingCard() and torn down in disconnect().
        this.activeMappingCardElement = null
        this.activeMappingCardAnchor = null
        this.activeMappingCardDeviceId = null
        this.boundDismissMappingCardOnOutsideClick = null

        // Setup event listeners
        // Drag is a first-class behavior: only attach drag listeners (and apply the
        // grab cursor / persistence path they enable) when dragging is enabled.
        // When dragValue is false, no mousedown/mousemove/mouseup/touchstart/
        // touchmove/touchend/dragstart drag listeners are attached. Since `drag`
        // defaults to true, all existing pages continue to attach drag listeners
        // exactly as today. The floor-plan click listener and icon click/double-click
        // listeners below are separate from drag and are NOT gated by dragValue.
        if (this.dragValue) {
            this.setupDragListeners()
        }
        
        // Setup click listener for floor plan dropping (if enabled)
        if (this.checkDroppingEnabled()) {
            this.setupFloorPlanClickListener()
        }
        
        // Setup icon click listeners after a brief delay to ensure page is fully initialized
        // This prevents race conditions with page load and table rendering
        setTimeout(() => {
            if (!this.iconClickListenersSetup) {
                this.setupIconClickListeners()
            }
        }, 100)

        // Initial render
        this.renderIcons()

        // Set initial button state
        this.updateToggleButtonState()
    }

    /**
     * Maps legacy Stimulus values onto the new configurable behavior system.
     *
     * Called during connect() after values are read from the DOM. Legacy values
     * are only applied when the corresponding new value attribute is NOT explicitly
     * present on the element, so an explicit new value always wins.
     *
     * - enableDoubleClick === false  -> doubleClickAction: 'none'
     * - enableTooltips === false     -> tooltipMode: 'none'
     */
    applyBackwardCompatibility() {
        const identifier = 'floorplans--floor-plan-icon-manager'

        // Legacy: enableDoubleClick === false maps to doubleClickAction: 'none'
        // Only apply when the new double-click-action value was not explicitly set.
        const doubleClickAttr = `data-${identifier}-double-click-action-value`
        if (!this.element.hasAttribute(doubleClickAttr) && !this.enableDoubleClickValue) {
            this.doubleClickActionValue = 'none'
        }

        // Legacy: enableTooltips === false maps to tooltipMode: 'none'
        // Only apply when the new tooltip-mode value was not explicitly set.
        const tooltipAttr = `data-${identifier}-tooltip-mode-value`
        if (!this.element.hasAttribute(tooltipAttr) && !this.enableTooltipsValue) {
            this.tooltipModeValue = 'none'
        }
    }

    /**
     * Validates the configured behavior actions against their allowed value sets.
     *
     * Called during connect() immediately after applyBackwardCompatibility(). Any
     * unrecognized action string is logged as a console warning and safely coerced
     * to 'none' so no invalid configuration ever reaches the behavior dispatchers.
     * This method never throws, guaranteeing connect() resolves to a valid behavior
     * set for any combination of supplied values (Requirement 1.3, Requirement 8
     * fallback behavior).
     */
    resolveBehaviorConfig() {
        const allowedActions = {
            clickActionValue: ['map-print-class', 'select', 'none'],
            holdActionValue: ['show-mapping-card', 'edit-device', 'none'],
            doubleClickActionValue: ['edit-device', 'none']
        }

        Object.entries(allowedActions).forEach(([valueKey, allowed]) => {
            const current = this[valueKey]
            if (!allowed.includes(current)) {
                console.warn(
                    `[floorplans--floor-plan-icon-manager] Unrecognized ${valueKey} ` +
                    `"${current}". Falling back to 'none'. Allowed values: ${allowed.join(', ')}.`
                )
                this[valueKey] = 'none'
            }
        })
    }

    disconnect() {
        this.clearUpdateTimeout()
        this.removeDroppedIcon()

        // Clean up touch timers
        if (this.touchTimer) {
            clearTimeout(this.touchTimer)
            this.touchTimer = null
        }
        
        // Clean up icon click listeners
        if (this.iconClickListenersSetup && this.hasDeviceListTarget) {
            // Remove the single-click handler if it was attached.
            if (this.boundHandleIconClick) {
                this.deviceListTarget.removeEventListener('click', this.boundHandleIconClick)
                this.boundHandleIconClick = null
            }
            if (this.isMobile) {
                if (this.boundHandleDeviceIconTouchStart) {
                    this.deviceListTarget.removeEventListener('touchstart', this.boundHandleDeviceIconTouchStart)
                }
                if (this.boundHandleDeviceIconTouchEnd) {
                    this.deviceListTarget.removeEventListener('touchend', this.boundHandleDeviceIconTouchEnd)
                }
                if (this.boundHandleDeviceIconTouchMove) {
                    this.deviceListTarget.removeEventListener('touchmove', this.boundHandleDeviceIconTouchMove)
                }
            } else {
                if (this.boundHandleDeviceIconDoubleClick) {
                    this.deviceListTarget.removeEventListener('dblclick', this.boundHandleDeviceIconDoubleClick)
                }
            }

            // Remove press-and-hold detection listeners (attached for both mouse and
            // touch when holdAction !== 'none', independent of isMobile).
            if (this.boundHandleHoldStartMouse) {
                this.deviceListTarget.removeEventListener('mousedown', this.boundHandleHoldStartMouse)
                this.boundHandleHoldStartMouse = null
            }
            if (this.boundHandleHoldEndMouse) {
                this.deviceListTarget.removeEventListener('mouseup', this.boundHandleHoldEndMouse)
                this.deviceListTarget.removeEventListener('mouseleave', this.boundHandleHoldEndMouse)
                this.boundHandleHoldEndMouse = null
            }
            if (this.boundHandleHoldStartTouch) {
                this.deviceListTarget.removeEventListener('touchstart', this.boundHandleHoldStartTouch)
                this.boundHandleHoldStartTouch = null
            }
            if (this.boundHandleHoldEndTouch) {
                this.deviceListTarget.removeEventListener('touchend', this.boundHandleHoldEndTouch)
                this.deviceListTarget.removeEventListener('touchcancel', this.boundHandleHoldEndTouch)
                this.boundHandleHoldEndTouch = null
            }
            if (this.boundHandleHoldCancelTouch) {
                this.deviceListTarget.removeEventListener('touchmove', this.boundHandleHoldCancelTouch)
                this.boundHandleHoldCancelTouch = null
            }

            this.iconClickListenersSetup = false
        }

        // Clear any pending hold timer to avoid a stray callback after teardown.
        if (this.holdTimer) {
            clearTimeout(this.holdTimer)
            this.holdTimer = null
        }

        // Remove any open mapping card and its document-level dismissal listener so
        // no card element or listener leaks after the controller detaches.
        this.dismissMappingCard()

        // Cancel any pending coalesced render so a scheduled frame does not fire after
        // the controller has detached (which would touch a torn-down deviceListTarget).
        if (this._renderRafId !== null) {
            if (typeof cancelAnimationFrame === 'function') {
                cancelAnimationFrame(this._renderRafId)
            } else {
                clearTimeout(this._renderRafId)
            }
            this._renderRafId = null
        }
    }

    // =============================================================================
    // STORE REACTIONS - These methods control when floor plan icons are updated
    // =============================================================================
    
    /**
     * React to area store changes
     * 
     * When the selected area changes, clear all icons and re-render.
     * This typically happens when users select a different area in dropdowns.
     */
    onAreaStoreUpdate() {
        // When area changes, clear everything including dropped icons. Coalesced with the
        // other store callbacks fired by the same area load so the icons rebuild once.
        // clearDropped preserves the previous clearAllIcons() + renderIcons() behavior.
        this.scheduleRender({ clearDropped: true })
    }

    /**
     * React to devices store changes
     * 
     * CRITICAL: This is the primary trigger for floor plan icon rendering.
     * When allDevicesStore is updated (by forms--item-form, area-device-manager, etc.),
     * this method re-renders all icons to reflect the new device data.
     * 
     * Common scenarios that trigger this:
     * - Update Device page: forms--item-form populates store with current device
     * - Area selection: area-device-manager loads devices for selected area  
     * - Device creation: new devices added to store after API calls
     * - Device updates: modified devices replace existing entries in store
     */
    onAllDevicesStoreUpdate() {
        this.scheduleRender()
    }

    /**
     * React to device types store changes (for icon lookups)
     * 
     * When allDeviceTypesStore is updated, re-render icons to use the latest
     * device type definitions and icon classes. This ensures icons display
     * correctly even if device types are modified.
     */
    onAllDeviceTypesStoreUpdate() {
        // When device types change, clear everything including dropped icons, coalesced
        // with the other store callbacks from the same load.
        this.scheduleRender({ clearDropped: true })

        // Update button state based on area selection
        this.updateToggleButtonState()
    }

    /**
     * React to bundles store changes (for icon lookups)
     */
    onAllBundlesStoreUpdate() {
        this.scheduleRender()
    }

    /**
     * React to deployment statuses store changes (for color lookups)
     */
    onAllDeploymentStatusesStoreUpdate() {
        this.scheduleRender()
    }

    /**
     * React to device type store changes (for dropping)
     */
    onDeviceTypeStoreUpdate() {
        // When device type changes, remove any existing dropped icon
        // User will need to click again to place the new device type
        this.removeDroppedIcon()
    }

    /**
     * React to bundle store changes (for dropping)
     */
    onBundleStoreUpdate() {
        // When bundle changes, remove any existing dropped icon
        // User will need to click again to place the new bundle
        this.removeDroppedIcon()
    }

    // =============================================================================
    // ICON RENDERING (from floor_plan_icons_controller)
    // =============================================================================

    /**
     * Ensure the device list container has proper positioning context
     */
    ensureDeviceListPositioning() {
        if (this.hasDeviceListTarget) {
            this.deviceListTarget.style.position = 'relative'
        }
    }

    /**
     * Render all device and bundle icons on the floor plan
     * 
     * ICON RENDERING ARCHITECTURE:
     * ============================
     * 
     * This method is the core of floor plan icon rendering. Icons that appear on the floor plan
     * are controlled by TWO main data sources:
     * 
     * 1. DEVICES DATA (allDevicesStore):
     *    - Contains device/bundle objects with coordinates and metadata
     *    - Populated by various controllers (forms--item-form, area-device-manager, etc.)
     *    - Each device must have floorplan_x_coordinate and floorplan_y_coordinate
     *    - Devices without coordinates will not render icons
     * 
     * 2. DEVICE TYPES DATA (allDeviceTypesStore):
     *    - Contains device type definitions with icon classes
     *    - Used to determine which icon to display for each device
     *    - Falls back to defaultIcon if device type not found
     * 
     * RENDERING PROCESS:
     * ==================
     * 
     * For each device in allDevicesStore:
     * 1. Extract coordinates from device.floorplan_x_coordinate/floorplan_y_coordinate
     * 2. Look up device type icon from allDeviceTypesStore using device.device_type
     * 3. Create DOM element positioned absolutely at the coordinates
     * 4. Add tooltip, dragging, and click handlers
     * 
     * BUNDLE HANDLING:
     * ================
     * 
     * Bundles are rendered as single icons representing the entire bundle instance:
     * - Deduplicated by bundle_instance_id to show one icon per bundle
     * - Uses bundle icon instead of device type icon
     * - All devices in bundle share the same coordinates
     * 
     * COORDINATE SYSTEM:
     * ==================
     * 
     * Icons are positioned relative to the deviceList container using absolute positioning:
     * - X/Y coordinates are in pixels relative to the floor plan image
     * - Coordinates are stored in the database and retrieved via API
     * - Missing or invalid coordinates (NaN) prevent icon rendering
     */
    /**
     * Coalesce multiple render requests fired within the same tick/burst into a single
     * renderIcons() pass on the next animation frame.
     *
     * A single area load triggers several store callbacks back-to-back plus (on the
     * Visual Print Mapper) a setMappedDeviceIds() recolor. Rendering synchronously on
     * each would rebuild the entire icon DOM 5+ times per load and race the mapping-
     * status recolor / connection-line draw against each other. Routing those callbacks
     * through scheduleRender() collapses the burst into one rebuild.
     *
     * @param {Object} [options]
     * @param {boolean} [options.clearDropped=false] - Also clear dropped icons before the
     *   render (matches the previous clearAllIcons() + renderIcons() behavior used by the
     *   area / device-type callbacks). When any request in a coalesced burst asks to clear
     *   dropped icons, the batched pass honors it.
     */
    scheduleRender(options = {}) {
        if (options.clearDropped) {
            this._pendingClearDropped = true
        }

        // Already scheduled — the pending frame will pick up the latest store state.
        if (this._renderRafId !== null) {
            return
        }

        const runner = (typeof requestAnimationFrame === 'function')
            ? requestAnimationFrame
            : (cb) => setTimeout(cb, 0)

        this._renderRafId = runner(() => {
            this._renderRafId = null

            // Honor a dropped-icon clear requested by any coalesced request in this burst.
            if (this._pendingClearDropped) {
                this._pendingClearDropped = false
                this.clearAllIcons()
            }

            this.renderIcons()
            this.updateToggleButtonState()
        })
    }

    renderIcons() {
        // Clear existing icons to prevent duplicates
        // DIFF-BASED RECONCILE (replaces the previous clearIcons() full teardown/rebuild).
        //
        // On large floor plans the old path removed and recreated every icon DOM node
        // (plus tooltip create/dispose) on each coalesced scheduleRender() — including
        // every mapping-status recolor — which was the remaining source of visible lag.
        // Instead we now compute the DESIRED icon set and reconcile it against the icons
        // already in the DOM: unchanged ids are updated in place, new ids are created,
        // and stale ids are removed. clearIcons()/clearAllIcons() remain for the RESET
        // paths (area change, device-type change, teardown) — scheduleRender() calls
        // clearAllIcons() BEFORE renderIcons() when _pendingClearDropped is set, after
        // which the current-elements map below is empty and the reconcile correctly ADDs
        // everything fresh.

        // Rebuild the per-render id -> record lookup maps ONCE for this pass. Downstream
        // helpers (getDeviceTypeIcon, getDeploymentStatusColor, getDeviceTypeColor,
        // getBundleIcon, findDeviceById) read these maps instead of scanning the full
        // store arrays per device, so total rendering work scales linearly with the
        // device count rather than quadratically.
        this._buildLookupMaps()

        // Compute the desired set of icons (ordered descriptors keyed by their stable
        // element id) using the existing filter logic. When showExistingDevices is false
        // this returns [], so the reconcile below removes every non-dropped icon —
        // preserving the previous "render nothing" short-circuit.
        const desired = this._computeDesiredIcons()

        // Reconcile the desired set against what is already rendered (ADD/UPDATE/REMOVE).
        this._reconcileIcons(desired)

        // Initialize Bootstrap tooltips for any newly created / re-inited icons.
        // Idempotent: elements that already have a live instance are skipped.
        initializeTooltips(this.deviceListTarget)

        // Enable dragging for the current icon set (safe to re-run on updated icons).
        this.enableDraggingForIcons()

        // NOTE: the hold-to-show mapping card is a standalone <div> appended to the
        // deviceList container (see showMappingCard). It is NOT anchored to any icon
        // element and does not carry the data-icon-type / data-bs-toggle attributes
        // that the reconcile touches, so a store-driven re-render leaves it in place.
        // The connection-lines SVG is likewise a sibling without data-icon-type and is
        // never touched here. No re-anchoring is needed.
    }

    /**
     * Compute the ordered list of icon descriptors that SHOULD be rendered for the
     * current store state, applying the exact same filters renderIcons() used before the
     * diff refactor: the device-type filter, the hide-mapped filter, and the
     * bundle-collapse-vs-render-members-individually behavior.
     *
     * Each descriptor is { key, device, type } where:
     *   - key is the stable element id used by createIconElement (and read back off the
     *     DOM during reconcile): device.id for an individual device icon, and
     *     bundle_instance_id for a collapsed bundle icon.
     *   - type is 'device' or 'bundle'.
     *   - device is the source record (used to build or update the element).
     *
     * Returns [] when showExistingDevices is false (the "render nothing" case), so the
     * reconcile removes all non-dropped icons.
     *
     * @returns {Array<{key: string, device: Object, type: ('device'|'bundle')}>}
     */
    _computeDesiredIcons() {
        // Preserve the showExistingDevices === false short-circuit: nothing is desired.
        if (!this.showExistingDevices) {
            return []
        }

        // Get devices from the store - this is the primary data source for floor plan icons
        let devices = this.allDevicesStoreValue || []
        const processedBundles = new Set()

        // Apply device type filter if configured
        // When deviceTypeFilter is set (e.g., "sends_printouts"), only render devices
        // whose device type has that property set to true
        if (this.deviceTypeFilterValue) {
            const filterProp = this.deviceTypeFilterValue
            const deviceTypes = this.allDeviceTypesStoreValue || []
            const allowedDeviceTypeIds = new Set(
                deviceTypes
                    .filter(dt => dt[filterProp] === true || dt[filterProp] === 'true')
                    .map(dt => dt.id)
            )
            devices = devices.filter(device => {
                const deviceTypeId = device.device?.device_type?.id || device.device_type
                return allowedDeviceTypeIds.has(deviceTypeId)
            })
        }

        // Hide-mapped filter (Visual Print Mapper). When the "Hide devices with
        // mappings" toggle is on, drop any device whose id is in the mapped set so
        // only unmapped (red) devices render. Default false -> no change anywhere else.
        if (this.hideMappedDevices && this.mappedDeviceIds && this.mappedDeviceIds.size > 0) {
            devices = devices.filter(device => !this.mappedDeviceIds.has(device.id))
        }

        // When a device-type filter is configured (e.g. the Visual Print Mapper's
        // "sends_printouts" filter), the device list above has ALREADY been narrowed to
        // just the devices whose OWN device type matches the filter. In that mode we want
        // every surviving device — including one that happens to be a member of a bundle —
        // to render as its own individually-clickable device icon, so it can be mapped to
        // a printer and therefore appears in the print mapping export. Collapsing it into a
        // single bundle icon would let the user map only one arbitrary member and orphan
        // the rest (the export discovers workstations by their own device type, so a
        // mapping stuck on the "wrong" collapsed member never shows up). When no filter is
        // configured (walkthrough, add/update device, manage areas) we keep the original
        // bundle-collapse behavior: one deduplicated icon per bundle instance.
        const renderBundleMembersIndividually = !!this.deviceTypeFilterValue

        const descriptors = []
        devices.forEach((device) => {
            if (isBundle(device) && !renderBundleMembersIndividually) {
                // Collapsed bundle icon (deduplicate by bundle_instance_id). The element
                // id createIconElement uses for a bundle is bundle_instance_id.
                if (!processedBundles.has(device.bundle_instance_id)) {
                    processedBundles.add(device.bundle_instance_id)
                    // Skip icons without valid coordinates (they never rendered before).
                    if (this.getDeviceCoordinates(device)) {
                        descriptors.push({ key: String(device.bundle_instance_id), device, type: 'bundle' })
                    }
                }
            } else {
                // Individual device icon (element id is device.id). Under a device-type
                // filter this also covers filtered-in bundle members.
                if (this.getDeviceCoordinates(device)) {
                    descriptors.push({ key: String(device.id), device, type: 'device' })
                }
            }
        })

        return descriptors
    }

    /**
     * Reconcile the desired icon descriptors against the icons already in the DOM.
     *
     * - Scans the CURRENT non-dropped icon elements up front (keyed by their element id)
     *   so the reconcile is always accurate even if the DOM changed externally (and so a
     *   preceding clearAllIcons() leaves this map empty -> everything is ADDed).
     * - ADD: id in desired but not current -> build via the existing renderDeviceIcon /
     *   renderBundleIcon path and append.
     * - UPDATE: id in both -> mutate the existing element in place (position, badge color,
     *   icon class, tooltip title). Dropped-icon elements are never in this map, so they
     *   are never touched.
     * - REMOVE: id in current but not desired -> dispose that one element's tooltip via
     *   the module Tooltip helper (so no stale transition callback can fire against a
     *   removed element), then remove the element.
     *
     * @param {Array<{key: string, device: Object, type: ('device'|'bundle')}>} descriptors
     */
    _reconcileIcons(descriptors) {
        if (!this.hasDeviceListTarget) {
            return
        }

        // Build the map of currently-rendered NON-dropped icon elements keyed by element id.
        const current = new Map()
        const renderedIcons = this.deviceListTarget.querySelectorAll(
            'div[data-icon-type]:not([data-is-dropped-icon="true"])'
        )
        renderedIcons.forEach((el) => {
            // el.id is the stable key set by createIconElement (device.id or
            // bundle_instance_id). Guard against an empty id just in case.
            if (el.id) {
                current.set(el.id, el)
            }
        })

        const desiredKeys = new Set()

        // ADD + UPDATE pass.
        descriptors.forEach((descriptor) => {
            desiredKeys.add(descriptor.key)
            const existing = current.get(descriptor.key)

            if (existing) {
                // UPDATE in place.
                this._updateIconElement(existing, descriptor)
            } else {
                // ADD via the existing element-building path (reuses createIconElement).
                if (descriptor.type === 'bundle') {
                    this.renderBundleIcon(descriptor.device)
                } else {
                    this.renderDeviceIcon(descriptor.device)
                }
            }
        })

        // REMOVE pass: any currently-rendered id not in the desired set.
        current.forEach((el, key) => {
            if (!desiredKeys.has(key)) {
                // Dispose this one element's tooltip BEFORE removing it so no queued
                // transition callback fires against a detached element.
                disposeTooltip(el)
                el.remove()
            }
        })
    }

    /**
     * Update an already-rendered icon element in place to match a desired descriptor.
     *
     * Touches only what can change between renders — the element's position, badge
     * background color, the Font Awesome icon class, and (when tooltips are enabled) the
     * tooltip title. The element id and delegated-listener-critical dataset attributes
     * (deviceId / bundleInstanceId / iconType) are preserved because the element stays in
     * place; they are refreshed defensively from the current device record.
     *
     * Tooltip safety: the tooltip instance is only disturbed when the title actually
     * changed. In that case the single element's tooltip instance is disposed via the
     * module Tooltip helper and the title/data attributes are updated; renderIcons() then
     * calls initializeTooltips() which recreates a fresh (animation:false) instance for
     * this element. Unchanged titles leave the live tooltip instance untouched.
     *
     * @param {HTMLElement} el - The existing icon wrapper element
     * @param {{key: string, device: Object, type: ('device'|'bundle')}} descriptor
     */
    _updateIconElement(el, descriptor) {
        const { device, type } = descriptor

        const coordinates = this.getDeviceCoordinates(device)
        if (coordinates) {
            const nextTop = `${ coordinates.y }px`
            const nextLeft = `${ coordinates.x }px`
            if (el.style.top !== nextTop) {
                el.style.top = nextTop
            }
            if (el.style.left !== nextLeft) {
                el.style.left = nextLeft
            }
        }

        // Badge background color (drives clause 3.1 green/red mapping-status recolor).
        const badgeSpan = el.querySelector('span.badge')
        if (badgeSpan) {
            const nextColor = this.getIconColor(this.iconColorModeValue, device, this.iconFixedColorValue)
            if (badgeSpan.style.backgroundColor !== nextColor) {
                badgeSpan.style.backgroundColor = nextColor
            }
        }

        // Font Awesome icon class (mirrors createIconElement's fas -> fa normalization).
        const iconEl = el.querySelector('span.badge > i') || el.querySelector('i')
        if (iconEl) {
            const rawIcon = type === 'bundle' ? this.getBundleIcon(device) : this.getDeviceTypeIcon(device)
            const nextIconClass = rawIcon.replace(/\bfas\b/g, 'fa')
            if (iconEl.className !== nextIconClass) {
                iconEl.className = nextIconClass
            }
        }

        // Refresh identity dataset attributes defensively (they normally do not change,
        // but keeping them in sync guarantees the delegated click/hold/double-click
        // listeners on deviceListTarget continue to resolve this element correctly).
        if (type === 'bundle') {
            el.dataset.bundleId = device.bundle?.id || device.bundle_id
            el.dataset.bundleInstanceId = device.bundle_instance_id
            if (device.bundle?.name !== undefined) {
                el.dataset.bundleName = device.bundle?.name
            }
            el.dataset.deviceId = device.id
        } else {
            el.dataset.deviceId = device.id
            el.dataset.deviceName = device.name
        }

        // Tooltip title: only disturb the tooltip when the title actually changed.
        if (this.tooltipModeValue !== 'none') {
            let nextTitle = this.generateTooltip(this.tooltipModeValue, device, type)
            if (device && device.id) {
                const actionText = this.tooltipActionHint(type)
                if (actionText) {
                    nextTitle = `${ nextTitle }<br><em>${ actionText }</em>`
                }
            }

            // Bootstrap moves the original `title` into `data-bs-original-title` once a
            // tooltip is initialized; compare against whichever is present.
            const currentTitle = el.getAttribute('data-bs-original-title')
                ?? el.getAttribute('title')
                ?? ''

            if (currentTitle !== nextTitle) {
                // Dispose this element's tooltip so the queued/cached content can't leak,
                // then update the title attributes; initializeTooltips() re-creates it.
                disposeTooltip(el)
                el.setAttribute('data-bs-toggle', 'tooltip')
                el.setAttribute('data-bs-placement', 'top')
                el.setAttribute('data-bs-html', 'true')
                el.setAttribute('title', nextTitle)
                // Clear any stale original-title left over from the disposed instance so
                // initializeTooltips reads the fresh `title`.
                el.removeAttribute('data-bs-original-title')
            }
        }
    }

    /**
     * Render an individual device icon
     * 
     * DEVICE ICON RENDERING:
     * ======================
     * 
     * This method renders a single device icon on the floor plan using:
     * 1. Device coordinates from the device object (floorplan_x_coordinate, floorplan_y_coordinate)
     * 2. Device type icon from allDeviceTypesStore (looked up by device.device_type)
     * 3. Device metadata for tooltips and interactions
     * 
     * The device object comes from allDevicesStore and must contain:
     * - Valid coordinates (non-NaN numbers)
     * - Device type reference for icon lookup
     * - Name and other metadata for tooltips
     */
    renderDeviceIcon(device) {
        const coordinates = this.getDeviceCoordinates(device)

        if (!coordinates) {
            return
        }

        const icon = this.getDeviceTypeIcon(device)
        const iconElement = this.createIconElement(icon, coordinates, 'device', device.id, device)

        // Add device-specific data attributes
        iconElement.dataset.deviceId = device.id
        iconElement.dataset.deviceName = device.name

        this.deviceListTarget.appendChild(iconElement)
    }

    /**
     * Render a bundle icon (represents the entire bundle instance)
     * 
     * BUNDLE ICON RENDERING:
     * ======================
     * 
     * Bundles are rendered as single icons representing all devices/parts in the bundle:
     * 1. Uses bundle coordinates (shared by all items in the bundle)
     * 2. Uses bundle icon from allBundlesStore instead of device type icon
     * 3. Represents the entire bundle instance, not individual devices
     * 
     * Bundle deduplication happens in renderIcons() to ensure only one icon per bundle_instance_id.
     */
    renderDeviceIcon(device) {
        const coordinates = this.getDeviceCoordinates(device)

        if (!coordinates) {
            return
        }

        const icon = this.getDeviceTypeIcon(device)
        const iconElement = this.createIconElement(icon, coordinates, 'device', device.id, device)

        // Add device-specific data attributes
        iconElement.dataset.deviceId = device.id
        iconElement.dataset.deviceName = device.name

        this.deviceListTarget.appendChild(iconElement)
    }

    /**
     * Render a bundle icon (represents the entire bundle instance)
     */
    renderBundleIcon(device) {
        const coordinates = this.getDeviceCoordinates(device)
        if (!coordinates) {
            return
        }

        const icon = this.getBundleIcon(device)
        const iconElement = this.createIconElement(icon, coordinates, 'bundle', device.bundle_instance_id, device)

        // Add bundle-specific data attributes
        iconElement.dataset.bundleId = device.bundle?.id || device.bundle_id
        iconElement.dataset.bundleInstanceId = device.bundle_instance_id
        iconElement.dataset.bundleName = device.bundle?.name
        iconElement.dataset.deviceId = device.id // Add device ID for dragging support

        this.deviceListTarget.appendChild(iconElement)
    }

    /**
     * Create an icon element with positioning (matching existing devices partial logic)
     */
    createIconElement(iconClass, coordinates, type, id, device) {
        // Create the main container div
        const iconAndTooltipWrapperDiv = document.createElement('div')
        iconAndTooltipWrapperDiv.id = id
        iconAndTooltipWrapperDiv.style.position = 'absolute'
        iconAndTooltipWrapperDiv.style.top = `${ coordinates.y }px`
        iconAndTooltipWrapperDiv.style.left = `${ coordinates.x }px`
        iconAndTooltipWrapperDiv.style.width = '30px'
        iconAndTooltipWrapperDiv.style.height = '30px'
        iconAndTooltipWrapperDiv.style.display = 'block'
        iconAndTooltipWrapperDiv.style.textAlign = 'center'
        iconAndTooltipWrapperDiv.style.lineHeight = '30px'

        // Add type-specific CSS classes
        if (type === 'bundle') {
            iconAndTooltipWrapperDiv.className = 'bundle-icon'
        }

        // Add tooltip attributes directly to the wrapper div.
        // Tooltip setup is routed through tooltipModeValue: 'none' disables tooltip
        // setup entirely, while 'device-info'/'print-mapping' produce mode-specific
        // content. Defaults + legacy mapping (enableTooltips === false -> 'none')
        // reproduce the prior enableTooltipsValue-gated behavior.
        if (this.tooltipModeValue !== 'none') {
            iconAndTooltipWrapperDiv.setAttribute('data-bs-toggle', 'tooltip')
            iconAndTooltipWrapperDiv.setAttribute('data-bs-placement', 'top')
            iconAndTooltipWrapperDiv.setAttribute('data-bs-html', 'true')
            iconAndTooltipWrapperDiv.setAttribute('title', this.generateTooltip(this.tooltipModeValue, device, type))
        }

        // Create badge span element with Bootstrap classes
        const badgeSpan = document.createElement('span')
        badgeSpan.className = 'badge badge-pill badge-primary'
        
        // Add bundle-label class for bundle types
        if (type === 'bundle') {
            badgeSpan.className += ' bundle-label'
        }

        // Set badge background color based on the configured icon color mode.
        // Defaults (iconColorMode = 'deployment-status') reproduce the prior
        // status-derived color exactly.
        badgeSpan.style.backgroundColor = this.getIconColor(this.iconColorModeValue, device, this.iconFixedColorValue)

        // Create icon element for the device type
        const iconElement = document.createElement('i')
        // Ensure icon class uses 'fa' instead of 'fas' for proper Font Awesome rendering
        iconElement.className = iconClass.replace(/\bfas\b/g, 'fa')
        iconElement.style.fontSize = '16px'
        iconElement.style.color = 'white'

        // Add data attributes for identification
        iconAndTooltipWrapperDiv.dataset.iconType = type
        iconAndTooltipWrapperDiv.dataset.iconId = id

        // Make device and bundle icons clickable (but not dropped icons)
        if (device && device.id) {
            // Set cursor based on drag gating plus the configured interaction affordance.
            // When drag is enabled (default) this reproduces the prior behavior exactly;
            // when drag is disabled the icon never shows a grab cursor.
            iconAndTooltipWrapperDiv.style.cursor = this.cursorForDeviceIcon()

            // Append an interaction hint to the tooltip that reflects the icon's
            // configured behavior (hold vs double-click), only when tooltip setup
            // is enabled AND there is an interaction to hint at.
            if (this.tooltipModeValue !== 'none') {
                const actionText = this.tooltipActionHint(type)
                if (actionText) {
                    iconAndTooltipWrapperDiv.setAttribute('title',
                        iconAndTooltipWrapperDiv.getAttribute('title') + `<br><em>${ actionText }</em>`
                    )
                }
            }
        }

        // Assemble the structure: div > span > i
        badgeSpan.appendChild(iconElement)
        iconAndTooltipWrapperDiv.appendChild(badgeSpan)

        return iconAndTooltipWrapperDiv
    }

    /**
     * Build the interaction-hint line appended to an icon's tooltip, reflecting the
     * icon's CONFIGURED behavior rather than always assuming double-click.
     *
     * Precedence (a hold action, when configured, is the primary interaction hinted):
     *   - holdAction 'show-mapping-card' -> 'Hold to see mappings'
     *   - holdAction 'edit-device'       -> 'Hold to edit device' / '... bundle devices'
     *   - else doubleClickAction 'edit-device' -> the existing double-click / long-press hint
     *   - otherwise no hint (returns '')
     *
     * On touch devices a hold gesture is described as 'Press and hold'.
     *
     * @param {string} type - 'device' or 'bundle'
     * @returns {string} The hint text (without markup), or '' when there is nothing to hint.
     */
    tooltipActionHint(type) {
        const isBundleType = type === 'bundle'

        // A configured hold action is the primary interaction to hint at.
        if (this.holdActionValue === 'show-mapping-card') {
            return this.isMobile ? 'Press and hold to see mappings' : 'Hold to see mappings'
        }

        if (this.holdActionValue === 'edit-device') {
            if (isBundleType) {
                return this.isMobile ? 'Press and hold to edit bundle devices' : 'Hold to edit bundle devices'
            }
            return this.isMobile ? 'Press and hold to edit device' : 'Hold to edit device'
        }

        // Fall back to the double-click (mobile: long-press) edit hint, but only
        // when double-click editing is actually enabled.
        if (this.doubleClickActionValue === 'edit-device') {
            if (isBundleType) {
                return this.isMobile ? 'Long press to edit bundle devices' : 'Double-click to edit bundle devices'
            }
            return this.isMobile ? 'Long press to edit device' : 'Double-click to edit device'
        }

        // No configured click/hold interaction to hint at.
        return ''
    }

    /**
     * Generate tooltip HTML content for a device or bundle icon based on tooltip mode.
     *
     * This is the TooltipGenerator dispatch layer (design.md Component 5, Function 2).
     * It routes to mode-specific content:
     *   - 'device-info'   -> name / deployment-status / room markup (current behavior)
     *   - 'print-mapping' -> name / room / mapped print class names markup
     *   - 'none'          -> empty string (and tooltip setup is disabled by the caller)
     *
     * The method NEVER throws on missing device fields — absent values render as
     * empty/omitted. The 'device-info' branch reproduces today's tooltip content
     * exactly (Requirement 8.1, 8.2, 8.3).
     *
     * @param {string} mode - 'device-info' | 'print-mapping' | 'none'
     * @param {Object} device - The device (or bundle) data object
     * @param {string} type - 'device' or 'bundle'
     * @param {Object} [mappingData] - Optional print mapping data for 'print-mapping' mode
     * @returns {string} HTML string for the Bootstrap tooltip title, or '' for 'none'
     */
    generateTooltip(mode, device, type, mappingData) {
        // Guard against a missing device so we never throw.
        const safeDevice = device || {}

        switch (mode) {
            case 'none':
                return ''
            case 'print-mapping':
                return this.generatePrintMappingTooltip(safeDevice, type, mappingData)
            case 'device-info':
            default:
                return this.generateTooltipContent(safeDevice, type)
        }
    }

    /**
     * Generate the default device-info tooltip content for a device or bundle.
     *
     * Preserves the exact markup produced prior to the tooltip-mode refactor so the
     * default (tooltipMode === 'device-info') reproduces today's tooltip content.
     */
    generateTooltipContent(device, type) {
        // Get device name with fallback
        const deviceName = device.name

        // Get deployment status name with fallback
        const deploymentStatus = device.deployment_status?.name

        // Get room name with fallback
        const roomName = device.locale?.room?.name

        if (type === 'bundle') {
            const bundleName = device.bundle?.name
            return `${ bundleName }<br>${ roomName }`
        } else {
            return `${ deviceName }<br>${ deploymentStatus }<br>${ roomName }`
        }
    }

    /**
     * Generate the print-mapping tooltip content: device name, room, and the list of
     * mapped print class names.
     *
     * Print class names are resolved from the optional mappingData argument, tolerating
     * several shapes (an array of mappings, or an object with a `mappings` array). Each
     * entry may expose the class name as `printClassName`, `print_class_name`, or a
     * nested `print_class.name`. Missing fields render as empty/omitted and this method
     * never throws (Requirement 8.2).
     *
     * @param {Object} device - The device (or bundle) data object
     * @param {string} type - 'device' or 'bundle'
     * @param {Object|Array} [mappingData] - Print mapping data
     * @returns {string} HTML string for the tooltip title
     */
    generatePrintMappingTooltip(device, type, mappingData) {
        // Device/bundle name with fallback
        const name = type === 'bundle' ? device.bundle?.name : device.name

        // Room name with fallback
        const roomName = device.locale?.room?.name

        // Resolve the list of print class names from the flexible mappingData shape.
        const classNames = this.extractPrintClassNames(mappingData)

        const lines = [name, roomName]
        if (classNames.length > 0) {
            lines.push(`Print Classes: ${ classNames.join(', ') }`)
        }

        return lines.join('<br>')
    }

    /**
     * Extract mapped print class names from the flexible print mapping data shape.
     *
     * Accepts either an array of mapping entries or an object exposing a `mappings`
     * array. Each entry's class name may be found under `printClassName`,
     * `print_class_name`, or `print_class.name`. Null/undefined and non-string names
     * are omitted. Never throws for any input.
     *
     * @param {Object|Array} [mappingData]
     * @returns {string[]} Ordered list of resolved print class names
     */
    extractPrintClassNames(mappingData) {
        if (!mappingData) {
            return []
        }

        const mappings = Array.isArray(mappingData)
            ? mappingData
            : (Array.isArray(mappingData.mappings) ? mappingData.mappings : [])

        return mappings
            .map(mapping => {
                if (!mapping) {
                    return null
                }
                return mapping.printClassName ||
                    mapping.print_class_name ||
                    mapping.print_class?.name ||
                    null
            })
            .filter(className => typeof className === 'string' && className.length > 0)
    }

    /**
     * Get device coordinates from the device object
     */
    getDeviceCoordinates(device) {
        // Try different possible coordinate locations in the device object
        const x = device.locale?.floorplan_x_coordinate ||
            device.floorplan_x_coordinate ||
            device.coordinates?.x

        const y = device.locale?.floorplan_y_coordinate ||
            device.floorplan_y_coordinate ||
            device.coordinates?.y

        // Convert to numbers and validate
        const xCoord = parseFloat(x)
        const yCoord = parseFloat(y)

        if (isNaN(xCoord) || isNaN(yCoord)) {
            return null
        }

        return { x: xCoord, y: yCoord }
    }

    /**
     * Check if dropping should be enabled based on page context
     * Dropping is disabled on update pages where a device already exists with coordinates
     * Dropping is enabled on create pages (walkthrough) even if coordinate fields have values
     */
    checkDroppingEnabled() {
        // On update pages, check if device already has coordinates
        if (this.isUpdatePageValue) {
            // Check if coordinate targets exist
            if (!this.hasCoordinateXTarget || !this.hasCoordinateYTarget) {
                return false;
            }
            
            // Use Stimulus targets to check for existing coordinates
            // Coordinates are considered "existing" if both fields have non-empty values
            const xValue = this.coordinateXTarget.value
            const yValue = this.coordinateYTarget.value
            const hasExistingCoordinates = xValue && 
                                           yValue &&
                                           xValue.trim() !== '' &&
                                           yValue.trim() !== '';
            
            // Enable dropping if coordinates don't exist (allow user to place device/bundle)
            return !hasExistingCoordinates;
        }
        
        // On create pages (walkthrough), always allow dropping
        return true;
    }

    /**
     * Get device type icon from the device
     * 
     * ICON LOOKUP PROCESS:
     * ====================
     * 
     * This method determines which icon to display for a device by:
     * 1. Extracting device_type ID from the device object
     * 2. Looking up the device type in allDeviceTypesStore
     * 3. Returning the icon class from the device type definition
     * 4. Falling back to defaultIcon if device type not found
     * 
     * The allDeviceTypesStore must be populated with device type definitions
     * that include icon classes (e.g., "fas fa-laptop", "fas fa-printer").
     */
    /**
     * Build id -> record lookup maps for the current store contents, used to resolve a
     * device's type icon, deployment-status color, device-type color, bundle icon, and
     * the device itself by id without a per-lookup linear scan of the full store arrays.
     *
     * Called once at the top of renderIcons(). The maps are also rebuilt lazily by the
     * accessors below if they are consulted outside of a render pass (e.g. an interaction
     * calling findDeviceById before the first render), so callers never see a null map.
     */
    _buildLookupMaps() {
        this._deviceTypeById = new Map((this.allDeviceTypesStoreValue || []).map(dt => [dt.id, dt]))
        this._deploymentStatusById = new Map((this.allDeploymentStatusesStoreValue || []).map(ds => [ds.id, ds]))
        this._bundleById = new Map((this.allBundlesStoreValue || []).map(b => [b.id, b]))
        this._deviceById = new Map((this.allDevicesStoreValue || []).map(d => [d.id, d]))
    }

    getDeviceTypeIcon(device) {
        const deviceTypeId = device.device?.device_type?.id || device.device_type

        if (!deviceTypeId) {
            return this.getDefaultIcon()
        }

        // Look up device type in the precomputed map to get its icon.
        if (!this._deviceTypeById) {
            this._deviceTypeById = new Map((this.allDeviceTypesStoreValue || []).map(dt => [dt.id, dt]))
        }
        const deviceType = this._deviceTypeById.get(deviceTypeId)

        return deviceType?.icon || this.getDefaultIcon()
    }

    /**
     * Get bundle icon from the device's bundle
     */
    getBundleIcon(device) {
        const bundleId = device.bundle?.id || device.bundle_id

        if (!bundleId) {
            return this.getDefaultIcon()
        }

        if (!this._bundleById) {
            this._bundleById = new Map((this.allBundlesStoreValue || []).map(b => [b.id, b]))
        }
        const bundle = this._bundleById.get(bundleId)

        return bundle?.icon || this.getDefaultIcon()
    }

    /**
     * Get default icon when no specific icon is available
     */
    getDefaultIcon() {
        return this.defaultIconValue || 'icon-device'
    }

    /**
     * Get deployment status color from the device's deployment status
     */
    getDeploymentStatusColor(device) {
        const deploymentStatusId = device.deployment_status?.id || device.deployment_status_id

        if (!deploymentStatusId) {
            return '#6c757d' // Default gray color
        }

        if (!this._deploymentStatusById) {
            this._deploymentStatusById = new Map((this.allDeploymentStatusesStoreValue || []).map(ds => [ds.id, ds]))
        }
        const deploymentStatus = this._deploymentStatusById.get(deploymentStatusId)

        return deploymentStatus?.status_color || '#6c757d' // Default gray if not found
    }

    /**
     * Determine the badge background color for a device icon based on the
     * configured icon color mode.
     *
     * Preconditions:
     * - mode is one of: 'deployment-status', 'device-type', 'fixed', 'mapping-status'
     * - device is a device object (may have missing fields)
     *
     * Postconditions (P6 — Icon color is always valid):
     * - Always returns a non-null CSS color string; never null/undefined
     * - 'deployment-status' reproduces getDeploymentStatusColor(device) exactly
     * - 'device-type' maps from the device type category to a color
     * - 'fixed' returns the supplied fixedColor
     * - Unrecognized modes fall back to the default color
     *
     * @param {string} mode - The color mode
     * @param {Object} device - The device data object
     * @param {string} fixedColor - The fixed color value (used when mode is 'fixed')
     * @returns {string} A non-null CSS color string
     */
    getIconColor(mode, device, fixedColor) {
        const DEFAULT_COLOR = '#6c757d' // Default gray fallback

        switch (mode) {
            case 'deployment-status':
                // Reproduce the current status-derived color exactly
                return this.getDeploymentStatusColor(device)

            case 'device-type':
                return this.getDeviceTypeColor(device) || DEFAULT_COLOR

            case 'fixed':
                // Use the supplied fixed color, falling back to the configured
                // value and finally the default so we never return null/undefined
                return fixedColor || this.iconFixedColorValue || DEFAULT_COLOR

            case 'mapping-status':
                // Visual Print Mapper: green when the device has at least one print
                // mapping, otherwise the fixed/red (unmapped) color. The mapped-id set
                // is populated by the print-mapper--mapping-status controller via
                // setMappedDeviceIds(); when empty, every icon renders as unmapped/red.
                if (device && this.mappedDeviceIds && this.mappedDeviceIds.has(device.id)) {
                    return '#198754' // Bootstrap success green
                }
                return fixedColor || this.iconFixedColorValue || '#dc3545'

            default:
                // Unrecognized mode: fall back to the default color
                return DEFAULT_COLOR
        }
    }

    /**
     * Get a color derived from a device's device type category.
     * Looks the device type up in the store and returns its color if present.
     *
     * @param {Object} device - The device data object
     * @returns {string} A non-null CSS color string
     */
    getDeviceTypeColor(device) {
        const DEFAULT_COLOR = '#6c757d'
        const deviceTypeId = device?.device?.device_type?.id || device?.device_type

        if (!deviceTypeId) {
            return DEFAULT_COLOR
        }

        if (!this._deviceTypeById) {
            this._deviceTypeById = new Map((this.allDeviceTypesStoreValue || []).map(dt => [dt.id, dt]))
        }
        const deviceType = this._deviceTypeById.get(deviceTypeId)

        // Fall back to a color keyed on the device type category when the device
        // type record carries no explicit color.
        return deviceType?.color
            || this.colorForCategory(deviceType?.category)
            || DEFAULT_COLOR
    }

    /**
     * Map a device type category string to a stable CSS color.
     *
     * @param {string} [category] - The device type category
     * @returns {string|null} A CSS color string, or null when no category maps
     */
    colorForCategory(category) {
        if (!category) {
            return null
        }

        const categoryColors = {
            printer: '#dc3545',
            workstation: '#0d6efd',
            server: '#198754',
            network: '#fd7e14',
            display: '#6f42c1',
            peripheral: '#20c997'
        }

        return categoryColors[String(category).toLowerCase()] || null
    }

    /**
     * Clear all existing icons from the floor plan
     */
    clearIcons() {
        // Dispose of existing tooltips before removing elements. Uses the module Tooltip
        // reference (via disposeTooltips) — NOT the global `bootstrap`, which is undefined
        // under Shakapacker so the old guard never ran and instances leaked on every
        // re-render. Icons are re-initialized right after via initializeTooltips() in
        // renderIcons(), so scoping disposal to the whole deviceListTarget is safe: any
        // retained/re-rendered icon gets a fresh tooltip immediately.
        disposeTooltips(this.deviceListTarget)

        // Remove only existing device icons, preserve dropped icons
        const existingIcons = this.deviceListTarget.querySelectorAll('div[data-icon-type]:not([data-is-dropped-icon="true"])')
        existingIcons.forEach(icon => icon.remove())
    }

    /**
     * Clear all icons from the floor plan (including dropped icons)
     * Used when we need to completely reset the floor plan
     */
    clearAllIcons() {
        // Dispose of all tooltips before removing elements. Uses the module Tooltip
        // reference (via disposeTooltips) — NOT the global `bootstrap`, which is undefined
        // under Shakapacker so the old guard never ran and instances leaked on every
        // re-render.
        disposeTooltips(this.deviceListTarget)

        // Remove all icons (both existing devices and dropped icons) from device list
        const allIcons = this.deviceListTarget.querySelectorAll('div[data-icon-type]')
        allIcons.forEach(icon => icon.remove())
    }

    /**
     * Enable dragging for all rendered icons
     */
    enableDraggingForIcons() {
        if (!this.hasDeviceListTarget) {
            return
        }

        const icons = this.deviceListTarget.querySelectorAll('[data-icon-type]')
        icons.forEach(icon => {
            // Set cursor based on whether it's a clickable device/bundle or just draggable.
            // When drag is disabled (dragValue === false) icons are never given the
            // grab affordance regardless of type — they are fixed click/hold targets.
            if (icon.dataset.deviceId && icon.dataset.isDroppedIcon !== 'true') {
                // Existing devices and bundles
                if (this.dragValue) {
                    // Preserve prior behavior exactly: mobile shows grab, desktop pointer.
                    icon.style.cursor = this.isMobile ? 'grab' : 'pointer'
                } else {
                    // Drag disabled: no grab affordance.
                    icon.style.cursor = this.cursorForDeviceIcon()
                }
            } else if (this.dragValue) {
                // Dropped icons are only draggable (and only when drag is enabled)
                icon.style.cursor = 'grab'
            } else {
                icon.style.cursor = 'default'
            }
            icon.draggable = false // Disable native drag to use custom implementation
        })
    }

    /**
     * Determine the resting cursor for a clickable device/bundle icon.
     *
     * - When dragging is enabled (dragValue === true), this reproduces the prior
     *   behavior exactly: mobile shows 'grab' and desktop shows 'pointer' when a
     *   double-click action is configured, otherwise 'grab' (the draggable affordance).
     * - When dragging is disabled (dragValue === false), icons are never given a
     *   'grab' cursor. They show 'pointer' when a click or double-click action is
     *   configured, and 'default' otherwise.
     *
     * @returns {string} A CSS cursor value ('grab' | 'pointer' | 'default').
     */
    cursorForDeviceIcon() {
        if (this.dragValue) {
            // Existing behavior (unchanged): keyed on the double-click affordance.
            if (this.doubleClickActionValue !== 'none') {
                return this.isMobile ? 'grab' : 'pointer'
            }
            return 'grab'
        }

        // Drag disabled: no grab affordance. Show a pointer only if the icon is
        // interactive via click or double-click; otherwise a plain default cursor.
        const hasClickAffordance =
            this.clickActionValue !== 'none' || this.doubleClickActionValue !== 'none'
        return hasClickAffordance ? 'pointer' : 'default'
    }

    // =============================================================================
    // DEVICE & BUNDLE DROPPING (from floor_plan_device_drop_controller)
    // =============================================================================

    /**
     * Setup click listener for the floor plan (for dropping new icons)
     */
    setupFloorPlanClickListener() {
        if (this.hasDropZoneTarget) {
            this.dropZoneTarget.addEventListener('click', this.handleFloorPlanClick.bind(this))
        }
    }

    /**
     * Setup click/touch listeners for device and bundle icons (for navigation)
     */
    setupIconClickListeners() {
        // Prevent duplicate listener setup
        if (this.iconClickListenersSetup) {
            return
        }
        
        // Only if the device list target exists
        if (this.hasDeviceListTarget) {
            // Single-click handler (ClickHandler dispatch). Attach ONLY when a click
            // action is configured (clickActionValue !== 'none'). Since clickAction
            // defaults to 'none', existing pages attach NO click listener and their
            // behavior is preserved. Attached independently of double-click gating so
            // a page can configure click behavior without also enabling double-click.
            if (this.clickActionValue !== 'none') {
                this.boundHandleIconClick = this.handleIconClick.bind(this)
                this.deviceListTarget.addEventListener('click', this.boundHandleIconClick)
            }

            // Press-and-hold detection (HoldHandler). Attach ONLY when a hold action
            // is configured (holdActionValue !== 'none'). Since holdAction defaults to
            // 'none', existing pages wire up NO hold listeners and their behavior is
            // preserved. This is set up before the double-click early-return below so
            // hold detection works on pages that disable double-click but enable a
            // hold gesture (e.g. the Visual Print Mapper: doubleClickAction 'none',
            // holdAction 'show-mapping-card'). Hold detection and the mobile
            // long-press-as-double-click path are mutually exclusive: the mobile
            // long-press touch listeners below are only attached when holdAction is
            // 'none', so there is no double-firing on the touch path.
            if (this.holdActionValue !== 'none') {
                this.setupHoldListeners()
            }

            // Skip the remaining double-click / long-press setup when double-click is
            // disabled ('none') so no navigation listeners are attached. The click and
            // hold listeners above are intentionally set up before this early return,
            // and iconClickListenersSetup is flagged so cleanup runs on disconnect.
            if (this.doubleClickActionValue === 'none') {
                if (this.boundHandleIconClick || this.holdActionValue !== 'none') {
                    this.iconClickListenersSetup = true
                }
                return
            }

            if (this.isMobile) {
                // Mobile: long press serves as the double-click alternative.
                // Only attach it when holdAction is 'none' so the mobile long-press
                // path does not conflict with a configured hold gesture (e.g. the
                // Visual Print Mapper's show-mapping-card hold).
                if (this.holdActionValue === 'none') {
                    // Store bound references to allow proper cleanup
                    this.boundHandleDeviceIconTouchStart = this.handleDeviceIconTouchStart.bind(this)
                    this.boundHandleDeviceIconTouchEnd = this.handleDeviceIconTouchEnd.bind(this)
                    this.boundHandleDeviceIconTouchMove = this.handleDeviceIconTouchMove.bind(this)

                    this.deviceListTarget.addEventListener('touchstart', this.boundHandleDeviceIconTouchStart)
                    this.deviceListTarget.addEventListener('touchend', this.boundHandleDeviceIconTouchEnd)
                    this.deviceListTarget.addEventListener('touchmove', this.boundHandleDeviceIconTouchMove)
                }
            } else {
                // Desktop: use double-click
                // Store bound reference to allow proper cleanup
                this.boundHandleDeviceIconDoubleClick = this.handleDeviceIconDoubleClick.bind(this)
                this.deviceListTarget.addEventListener('dblclick', this.boundHandleDeviceIconDoubleClick)
            }
            
            // Mark as set up
            this.iconClickListenersSetup = true
        }
    }

    /**
     * Attach press-and-hold detection listeners to the device list container.
     *
     * MUST only be called when holdActionValue !== 'none' (gated in
     * setupIconClickListeners). Wires both the mouse path (mousedown/mouseup/
     * mouseleave) and the touch path (touchstart/touchend/touchmove/touchcancel)
     * so a long press is detected on desktop and mobile alike.
     *
     * The touch path here REPLACES the mobile long-press-as-double-click path:
     * setupIconClickListeners only attaches the mobile long-press touch listeners
     * when holdAction === 'none', so the two are mutually exclusive and never both
     * fire on the same touch gesture.
     *
     * Bound handler references are stored so disconnect() can remove every listener.
     */
    setupHoldListeners() {
        this.boundHandleHoldStartMouse = this.handleHoldStart.bind(this)
        this.boundHandleHoldEndMouse = this.handleHoldEnd.bind(this)
        this.boundHandleHoldStartTouch = this.handleHoldStart.bind(this)
        this.boundHandleHoldEndTouch = this.handleHoldEnd.bind(this)
        this.boundHandleHoldCancelTouch = this.handleHoldCancel.bind(this)

        // Mouse path: press starts the timer; release or leaving the container ends it.
        this.deviceListTarget.addEventListener('mousedown', this.boundHandleHoldStartMouse)
        this.deviceListTarget.addEventListener('mouseup', this.boundHandleHoldEndMouse)
        this.deviceListTarget.addEventListener('mouseleave', this.boundHandleHoldEndMouse)

        // Touch path: press starts the timer; release/cancel ends it; drift cancels it.
        this.deviceListTarget.addEventListener('touchstart', this.boundHandleHoldStartTouch)
        this.deviceListTarget.addEventListener('touchend', this.boundHandleHoldEndTouch)
        this.deviceListTarget.addEventListener('touchcancel', this.boundHandleHoldEndTouch)
        this.deviceListTarget.addEventListener('touchmove', this.boundHandleHoldCancelTouch)
    }

    /**
     * Begin a press-and-hold gesture over a clickable device icon.
     *
     * PRECONDITION: holdActionValue !== 'none'
     * POSTCONDITION: after holdDelayValue ms without release/cancel/drift, the hold
     *   action fires via handleHold and holdFired is set (so the ensuing click is
     *   suppressed by handleIconClick).
     * INVARIANT: at most one hold timer is active at a time — any existing timer is
     *   cleared before a new one starts.
     *
     * @param {MouseEvent|TouchEvent} event
     */
    handleHoldStart(event) {
        const iconElement = this.findClickableDeviceIcon(event.target)
        if (!iconElement) {
            return
        }

        // Enforce the single-active-timer invariant.
        if (this.holdTimer) {
            clearTimeout(this.holdTimer)
            this.holdTimer = null
        }

        this.holdTarget = iconElement
        this.holdStartPosition = this.pointerPositionFromEvent(event)

        this.holdTimer = setTimeout(() => {
            const device = this.findDeviceById(iconElement.dataset.deviceId)
            // Fire the configured hold action. handleHold dispatches on
            // holdActionValue (implemented in task 6.2).
            this.handleHold(this.holdActionValue, iconElement, device)

            // Mark the hold as fired so the synthetic click that follows the press
            // is suppressed in handleIconClick, and clear the timer handle.
            this.holdFired = true
            this.holdTimer = null
        }, this.holdDelayValue)
    }

    /**
     * End a pending press-and-hold gesture (mouseup / mouseleave / touchend /
     * touchcancel). If the timer has not yet fired, the pending hold is cancelled so
     * a short press resolves as a normal click instead of a hold.
     *
     * @param {MouseEvent|TouchEvent} event
     */
    handleHoldEnd(event) {
        if (this.holdTimer) {
            clearTimeout(this.holdTimer)
            this.holdTimer = null
        }
        this.holdTarget = null
        this.holdStartPosition = null
    }

    /**
     * Cancel a pending press-and-hold gesture when the pointer drifts more than 5px
     * from where the press began (touchmove). This prevents an accidental hold while
     * the user is moving, and — on non-draggable pages — guarantees a small drift
     * never fires the hold action. Does nothing once the timer has already fired.
     *
     * @param {TouchEvent|MouseEvent} event
     */
    handleHoldCancel(event) {
        if (!this.holdTimer || !this.holdStartPosition) {
            return
        }

        const position = this.pointerPositionFromEvent(event)
        const dx = position.x - this.holdStartPosition.x
        const dy = position.y - this.holdStartPosition.y

        if (Math.sqrt(dx * dx + dy * dy) > 5) {
            clearTimeout(this.holdTimer)
            this.holdTimer = null
            this.holdTarget = null
            this.holdStartPosition = null
        }
    }

    /**
     * Extract the pointer coordinates from a mouse or touch event.
     *
     * Mouse events expose clientX/clientY directly; touch events expose them on the
     * first entry of `touches` (start/move) or `changedTouches` (end/cancel). Falls
     * back to { x: 0, y: 0 } so it never throws for an unexpected event shape.
     *
     * @param {MouseEvent|TouchEvent} event
     * @returns {{x: number, y: number}}
     */
    pointerPositionFromEvent(event) {
        if (event.touches && event.touches.length > 0) {
            return { x: event.touches[0].clientX, y: event.touches[0].clientY }
        }
        if (event.changedTouches && event.changedTouches.length > 0) {
            return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY }
        }
        return { x: event.clientX || 0, y: event.clientY || 0 }
    }

    /**
     * Dispatch the configured hold (long-press) action (design.md Component 3:
     * HoldHandler).
     *
     * Dispatches on the supplied hold action:
     *   - 'edit-device'       -> navigate to the device/bundle edit page (reuses
     *                            openDeviceOrBundle, same path as double-click edit)
     *   - 'show-mapping-card' -> show the print-mapping card via showMappingCard
     *   - 'none' / anything else -> no-op
     *
     * This dispatcher is total: it never throws for any action value. Unrecognized
     * action strings fall through to the no-op branch.
     *
     * @param {string} action - The configured hold action (holdActionValue)
     * @param {HTMLElement} iconElement - The held icon element
     * @param {Object} [device] - The device data object (from the store)
     */
    handleHold(action, iconElement, device) {
        switch (action) {
            case 'edit-device':
                this.openDeviceOrBundle(iconElement)
                break
            case 'show-mapping-card':
                this.showMappingCard(iconElement, device)
                break
            default:
                // 'none' and any unrecognized action fall through to a no-op.
                break
        }
    }

    /**
     * Show an interactive print-mapping card near the held icon (design.md
     * Component 3: HoldHandler "showMappingCard"; Requirements 4.1, 4.2, 8.4, 8.6,
     * 8.7).
     *
     * The card is a plain, absolutely-positioned `<div class="floorplan-mapping-card">`
     * appended into the deviceList container (the same positioned container the icons
     * render into). It is deliberately NOT a Bootstrap popover anchored to the icon:
     * the icon manager destroys and re-creates every icon element on each store-driven
     * re-render, which would orphan a popover anchored to an icon and make it flash
     * away. As a sibling of the icons — and carrying neither `data-icon-type` nor
     * `data-bs-toggle="tooltip"` — the card is left untouched by clearIcons()/
     * clearAllIcons() and therefore persists across re-renders.
     *
     * The card fetches the device's print mappings live from the print mappings API
     * (`GET /api/print_mappings/workstations?area_id=<areaId>`) and renders the
     * workstation name, room, and a list of print mappings. Each mapping row carries a
     * delete control; the card also carries an explicit close (×) control. Deleting a
     * mapping issues `DELETE /api/print_mappings/delete` and, on success, removes the
     * row from the card DOM and emits a bubbling `floorplan:mapping-deleted` event; on
     * failure it shows an error notification and keeps the row (Req 8.7).
     *
     * Loading and error states:
     *   - A "Loading mappings…" placeholder is shown while the fetch is in flight.
     *   - If the fetch fails (Req 8.6), the card shows an error message instead of a
     *     mapping list rather than throwing.
     *
     * Invariants:
     *   - Only one mapping card is visible at a time. Any previously-shown card is
     *     dismissed before a new one is created.
     *   - Never throws: card construction is wrapped in try/catch and is a safe no-op
     *     when there is no deviceList container to render into.
     *   - The active card element is tracked on `this.activeMappingCardElement` so it
     *     can be dismissed by `dismissMappingCard()`, an outside click, the close
     *     control, or `disconnect()`.
     *
     * @param {HTMLElement} iconElement - The held icon element (used for positioning)
     * @param {Object} [device] - The device (or bundle) data object (from the store)
     */
    showMappingCard(iconElement, device) {
        if (!iconElement || !this.hasDeviceListTarget) {
            return
        }

        // Only one card at a time — dismiss any previously-shown card first.
        this.dismissMappingCard()

        const safeDevice = device || {}
        const deviceId = safeDevice.id || iconElement.dataset.deviceId
        const deviceName = safeDevice.name || iconElement.dataset.deviceName || 'Device'
        const roomName = safeDevice.locale?.room?.name || ''

        try {
            // Build the standalone card element. It is a sibling of the icons inside
            // deviceListTarget, so it survives icon re-renders (clearIcons only
            // removes div[data-icon-type] elements — the card carries no such attr).
            const card = document.createElement('div')
            card.className = 'floorplan-mapping-card'

            // Inline styles so the card renders without requiring any new CSS (there
            // is currently no stylesheet rule for .floorplan-mapping-card).
            card.style.position = 'absolute'
            card.style.zIndex = '1080'
            card.style.minWidth = '220px'
            card.style.maxWidth = '320px'
            card.style.backgroundColor = '#ffffff'
            card.style.border = '1px solid rgba(0, 0, 0, 0.175)'
            card.style.borderRadius = '0.375rem'
            card.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
            card.style.padding = '0.75rem'
            card.style.fontSize = '0.875rem'
            card.style.color = '#212529'
            card.style.textAlign = 'left'
            card.style.lineHeight = 'normal'

            // Position the card near the held icon. Prefer the icon's own inline
            // left/top (icons are positioned absolutely inside the same container);
            // fall back to a bounding-rect calculation relative to the container.
            const position = this.computeMappingCardPosition(iconElement)
            card.style.left = `${ position.left }px`
            card.style.top = `${ position.top }px`

            // Render the loading placeholder immediately for instant feedback.
            card.innerHTML = this.buildMappingCardLoadingHtml(deviceName, roomName)

            this.deviceListTarget.appendChild(card)


            this.activeMappingCardElement = card
            this.activeMappingCardAnchor = iconElement
            this.activeMappingCardDeviceId = deviceId

            // Record when the card opened so the outside-click handler can ignore
            // the synthetic click that follows the hold-release (mouseup -> click),
            // which otherwise dismisses the card the instant it appears.
            this.mappingCardShownAt = Date.now()

            // Dismiss the card on a genuine click outside the card/anchor.
            this.boundDismissMappingCardOnOutsideClick = (event) => {
                // Ignore clicks that land within a short grace window after the card
                // is shown. The hold gesture's release produces a document click
                // right after the 500ms timer fires the card; without this window
                // that release click would immediately dismiss it.
                if (Date.now() - this.mappingCardShownAt < 400) {
                    return
                }

                const cardEl = this.getMappingCardTipElement()
                if (cardEl && cardEl.contains(event.target)) {
                    return
                }

                // Compare against the LIVE icon for this device, not just the
                // originally-captured anchor node: the icon manager re-renders icons
                // on store updates, detaching/replacing the anchor, which would make
                // a captured-node contains() check spuriously report "outside".
                const anchorEl = this.activeMappingCardAnchor
                if (anchorEl && anchorEl.contains(event.target)) {
                    return
                }
                const liveIcon = this.activeMappingCardDeviceId
                    ? this.getIconByDeviceId(this.activeMappingCardDeviceId)
                    : null
                if (liveIcon && liveIcon.contains(event.target)) {
                    return
                }

                this.dismissMappingCard()
            }
            // Attach after the grace window so the hold-release click cannot be the
            // one that dismisses the freshly-shown card.
            setTimeout(() => {
                if (this.boundDismissMappingCardOnOutsideClick) {
                    document.addEventListener('click', this.boundDismissMappingCardOnOutsideClick)
                }
            }, 0)

            // Wire the close (×) control right away — it exists in the loading state
            // too so the user can dismiss while the fetch is in flight.
            this.attachMappingCardCloseHandler()

            // Fetch the mappings and update the card content once resolved. The card
            // element reference is captured so a late-arriving response for a card
            // that has since been dismissed/replaced is ignored.
            this.loadMappingCardContent(card, deviceId, deviceName, roomName)
        } catch (error) {
            // Never let card construction/rendering break the hold gesture.
            console.warn('floorplans--floor-plan-icon-manager: unable to show mapping card', error)
            this.dismissMappingCard()
        }
    }

    /**
     * Compute the absolute left/top (in pixels, relative to the deviceList
     * container) at which to render the mapping card near the held icon.
     *
     * Prefers the icon's own inline `style.left`/`style.top` (icons are positioned
     * absolutely inside the same container, so these are already container-relative).
     * Falls back to a getBoundingClientRect() difference against the container when
     * inline coordinates are unavailable. The card is offset slightly down/right of
     * the icon so it does not sit directly on top of it.
     *
     * @param {HTMLElement} iconElement - The held icon element
     * @returns {{ left: number, top: number }}
     */
    computeMappingCardPosition(iconElement) {
        const OFFSET_X = 34
        const OFFSET_Y = 0

        const inlineLeft = parseFloat(iconElement.style.left)
        const inlineTop = parseFloat(iconElement.style.top)

        if (!Number.isNaN(inlineLeft) && !Number.isNaN(inlineTop)) {
            return { left: inlineLeft + OFFSET_X, top: inlineTop + OFFSET_Y }
        }

        try {
            const iconRect = iconElement.getBoundingClientRect()
            const containerRect = this.deviceListTarget.getBoundingClientRect()
            return {
                left: (iconRect.left - containerRect.left) + OFFSET_X,
                top: (iconRect.top - containerRect.top) + OFFSET_Y
            }
        } catch (_error) {
            return { left: 0, top: 0 }
        }
    }

    /**
     * Fetch the print mappings for the held device and update the mapping card
     * content once resolved (Requirements 4.1, 8.6).
     *
     * Uses `GET /api/print_mappings/workstations?area_id=<areaId>` — the documented
     * source of print mappings including the mapping ids required for deletion — and
     * finds the workstation matching the held icon's deviceId. On any failure the
     * card is rendered with an error message instead of a list (never throws).
     *
     * @param {HTMLElement} card - The mapping-card <div> shown for this device
     * @param {string} deviceId - The held device's id (workstation id)
     * @param {string} deviceName - Display name for the card header
     * @param {string} roomName - Display room for the card header
     */
    async loadMappingCardContent(card, deviceId, deviceName, roomName) {
        const areaId = this.areaStoreValue?.id

        try {
            if (!areaId) {
                throw new Error('No area selected for print mappings lookup')
            }

            // Targeted single-workstation lookup so the card does not fetch and
            // client-side-filter the entire area's workstation list (perf regression fix).
            const url = `/api/print_mappings/workstation?workstation_id=${ encodeURIComponent(deviceId) }`
            const response = await fetch(url, {
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP ${ response.status }: ${ response.statusText }`)
            }

            const data = await response.json()
            const workstation = (data && data.workstation) || null
            const mappings = (workstation && workstation.print_mappings) || []
            // Prefer the freshly-resolved room name from the API when present.
            const resolvedRoom = (workstation && workstation.room_name) || roomName

            // Ignore late responses for a card that is no longer the active one.
            if (this.activeMappingCardElement !== card) {
                return
            }

            this.updateMappingCardContent(
                card,
                this.buildMappingCardHtml(deviceName, resolvedRoom, mappings)
            )
            this.attachMappingCardCloseHandler()
            this.attachMappingCardDeleteHandlers()
        } catch (error) {
            console.warn('floorplans--floor-plan-icon-manager: unable to load mappings', error)

            if (this.activeMappingCardElement !== card) {
                return
            }

            // Requirement 8.6: show the card with an error message, not a list.
            this.updateMappingCardContent(
                card,
                this.buildMappingCardErrorHtml(deviceName, roomName)
            )
            this.attachMappingCardCloseHandler()
        }
    }

    /**
     * Build the loading-state HTML for the mapping card (Requirement 8.4).
     *
     * @param {string} deviceName
     * @param {string} roomName
     * @returns {string} HTML string for the card content
     */
    buildMappingCardLoadingHtml(deviceName, roomName) {
        return `
            ${ this.buildMappingCardHeaderHtml(deviceName, roomName) }
            <div class="floorplan-mapping-card-body">
                <div class="text-muted small">Loading mappings…</div>
            </div>
        `
    }

    /**
     * Build the error-state HTML for the mapping card (Requirement 8.6).
     *
     * @param {string} deviceName
     * @param {string} roomName
     * @returns {string} HTML string for the card content
     */
    buildMappingCardErrorHtml(deviceName, roomName) {
        return `
            ${ this.buildMappingCardHeaderHtml(deviceName, roomName) }
            <div class="floorplan-mapping-card-body">
                <div class="text-danger small">Could not load mappings. Please try again.</div>
            </div>
        `
    }

    /**
     * Build the populated HTML for the mapping card: header plus a list of print
     * mappings, each with a delete control (Requirements 4.1, 8.7).
     *
     * @param {string} deviceName
     * @param {string} roomName
     * @param {Array<Object>} mappings - Print mappings from the workstations API
     * @returns {string} HTML string for the card content
     */
    buildMappingCardHtml(deviceName, roomName, mappings) {
        const safeMappings = Array.isArray(mappings) ? mappings : []

        let listHtml
        if (safeMappings.length === 0) {
            listHtml = '<div class="text-muted small">No print mappings.</div>'
        } else {
            const rows = safeMappings.map(mapping => {
                const className = this.escapeHtml(mapping.print_class_name || mapping.print_class_id || 'Print class')
                const printerName = mapping.printer_name || mapping.printer_id
                const printerHtml = printerName
                    ? ` <span class="text-muted">(${ this.escapeHtml(printerName) })</span>`
                    : ''
                const mappingId = this.escapeHtml(mapping.id || '')

                return `
                    <li class="floorplan-mapping-row d-flex justify-content-between align-items-center" data-print-mapping-id="${ mappingId }">
                        <span class="floorplan-mapping-label">${ className }${ printerHtml }</span>
                        <button type="button"
                                class="btn btn-sm btn-link text-danger p-0 ms-2 floorplan-mapping-delete"
                                data-print-mapping-id="${ mappingId }"
                                aria-label="Delete mapping">
                            <i class="bi bi-trash"></i>
                        </button>
                    </li>
                `
            }).join('')

            listHtml = `<ul class="list-unstyled mb-0 floorplan-mapping-list">${ rows }</ul>`
        }

        return `
            ${ this.buildMappingCardHeaderHtml(deviceName, roomName) }
            <div class="floorplan-mapping-card-body">
                ${ listHtml }
            </div>
        `
    }

    /**
     * Build the shared header (name, room, close control) for the mapping card.
     *
     * @param {string} deviceName
     * @param {string} roomName
     * @returns {string} HTML string for the card header
     */
    buildMappingCardHeaderHtml(deviceName, roomName) {
        const name = this.escapeHtml(deviceName || 'Device')
        const room = roomName ? `<div class="text-muted small">${ this.escapeHtml(roomName) }</div>` : ''

        return `
            <div class="floorplan-mapping-card-header d-flex justify-content-between align-items-start mb-2">
                <div>
                    <div class="fw-bold">${ name }</div>
                    ${ room }
                </div>
                <button type="button"
                        class="btn-close btn-sm ms-2 floorplan-mapping-close"
                        aria-label="Close"></button>
            </div>
        `
    }

    /**
     * Escape a string for safe interpolation into the card HTML.
     *
     * @param {*} value
     * @returns {string}
     */
    escapeHtml(value) {
        if (value === null || value === undefined) {
            return ''
        }
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
    }

    /**
     * Resolve the active mapping card's DOM element.
     *
     * The card is a standalone <div>, so its "tip" element is simply the card
     * element itself. Retained (rather than inlined) so the existing close/delete/
     * outside-click helpers can keep calling a single accessor.
     *
     * @returns {HTMLElement|null}
     */
    getMappingCardTipElement() {
        return this.activeMappingCardElement || null
    }

    /**
     * Replace the visible content of the active mapping card <div> with new HTML.
     *
     * @param {HTMLElement} card - The mapping-card <div>
     * @param {string} html - The new content HTML
     */
    updateMappingCardContent(card, html) {
        if (!card) {
            return
        }
        card.innerHTML = html
    }

    /**
     * Attach the close (×) control handler on the active card's tip element so the
     * card can be dismissed explicitly (Requirement 8.4). Idempotent: replaces any
     * previously-attached listener via a bound reference.
     */
    attachMappingCardCloseHandler() {
        const tip = this.getMappingCardTipElement()
        if (!tip) {
            return
        }

        const closeButton = tip.querySelector('.floorplan-mapping-close')
        if (!closeButton) {
            return
        }

        // Avoid double-binding if content is refreshed and the same node persists.
        if (closeButton.dataset.closeBound === 'true') {
            return
        }
        closeButton.dataset.closeBound = 'true'
        closeButton.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopPropagation()
            this.dismissMappingCard()
        })
    }

    /**
     * Attach delete-control handlers to each mapping row on the active card's tip
     * element (Requirement 8.7). Clicking a delete control issues the delete request
     * and, on success, removes the row from the card DOM and emits
     * `floorplan:mapping-deleted`; on failure it shows an error and keeps the row.
     */
    attachMappingCardDeleteHandlers() {
        const tip = this.getMappingCardTipElement()
        if (!tip) {
            return
        }

        const deleteButtons = tip.querySelectorAll('.floorplan-mapping-delete')
        deleteButtons.forEach((button) => {
            if (button.dataset.deleteBound === 'true') {
                return
            }
            button.dataset.deleteBound = 'true'
            button.addEventListener('click', (event) => {
                event.preventDefault()
                event.stopPropagation()
                const printMappingId = button.dataset.printMappingId
                this.deleteMappingFromCard(printMappingId, button)
            })
        })
    }

    /**
     * Delete a single print mapping via `DELETE /api/print_mappings/delete`
     * (Requirement 8.7).
     *
     * On success: remove the corresponding row from the card DOM and emit a bubbling
     * `floorplan:mapping-deleted` event. On failure: show a failure notification via
     * `sendFailureNotification` and DO NOT remove the row.
     *
     * @param {string} printMappingId - The id of the print mapping to delete
     * @param {HTMLElement} button - The delete control that was clicked
     */
    async deleteMappingFromCard(printMappingId, button) {
        const workstationId = this.activeMappingCardDeviceId

        if (!printMappingId || !workstationId) {
            sendFailureNotification('Unable to remove print mapping. Please try again.')
            return
        }

        try {
            const response = await fetch('/api/print_mappings/delete', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({
                    workstation_id: workstationId,
                    print_mapping_id: printMappingId
                })
            })

            if (!response.ok) {
                let message = 'Failed to remove print mapping'
                try {
                    const errorData = await response.json()
                    message = errorData.message || message
                } catch (_error) {
                    // Ignore JSON parse errors and use the default message.
                }
                throw new Error(message)
            }

            // Parse the success response so the updated workstation payload (with the
            // remaining resolved print_mappings from `build_workstation_response`) can be
            // forwarded on the `floorplan:mapping-deleted` event. Listeners use it to
            // update the affected workstation locally instead of re-fetching the whole
            // area's workstation list (print-mapper-performance-regression, Property 1).
            let data = {}
            try {
                data = await response.json()
            } catch (_error) {
                // Tolerate an empty/non-JSON body; the event falls back to id-only detail.
            }

            // Success: remove the mapping row from the card DOM (Requirement 8.7).
            const row = button.closest('.floorplan-mapping-row')
            if (row) {
                row.remove()
            }

            // If no rows remain, reflect the empty state.
            const tip = this.getMappingCardTipElement()
            const list = tip ? tip.querySelector('.floorplan-mapping-list') : null
            if (list && list.children.length === 0) {
                const body = tip.querySelector('.floorplan-mapping-card-body')
                if (body) {
                    body.innerHTML = '<div class="text-muted small">No print mappings.</div>'
                }
            }

            // Emit a bubbling event so listeners (e.g. the Visual Print Mapper) can
            // react to the removed mapping.
            const anchor = this.activeMappingCardAnchor
            if (anchor) {
                anchor.dispatchEvent(new CustomEvent('floorplan:mapping-deleted', {
                    // ADDITIVE: include the delete response's updated workstation so
                    // listeners can update locally without a full-area refetch. Existing
                    // `deviceId` / `mappingId` fields are preserved for backward compat.
                    detail: { deviceId: workstationId, mappingId: printMappingId, workstation: data.workstation },
                    bubbles: true
                }))
            }

            sendSuccessNotification('Print mapping removed successfully')
        } catch (error) {
            // Requirement 8.7: on delete failure, notify and keep the entry in the DOM.
            console.error('floorplans--floor-plan-icon-manager: delete mapping failed', error)
            sendFailureNotification(error.message || 'Failed to remove print mapping')
        }
    }

    /**
     * Dismiss the active print-mapping card, if any (design.md Component 3:
     * HoldHandler "dismissMappingCard").
     *
     * Removes the tracked card <div> from the DOM, removes the document-level
     * outside-click listener, and clears all tracked state. Safe to call when no
     * card is active (no-op).
     */
    dismissMappingCard() {
        if (this.boundDismissMappingCardOnOutsideClick) {
            document.removeEventListener('click', this.boundDismissMappingCardOnOutsideClick)
            this.boundDismissMappingCardOnOutsideClick = null
        }

        if (this.activeMappingCardElement) {
            try {
                this.activeMappingCardElement.remove()
            } catch (_error) {
                // Removing an already-detached node can throw in exotic cases; ignore
                // so dismissal is always a safe operation.
            }
            this.activeMappingCardElement = null
        }

        this.activeMappingCardAnchor = null
        this.activeMappingCardDeviceId = null
    }

    /**
     * Detect if this is a mobile device
     */
    detectMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            ('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0)
    }

    /**
     * Dispatch single-click behavior based on the configured clickAction.
     *
     * This is the ClickHandler dispatch layer (design.md Component 2, Function 1).
     * It is attached to the deviceListTarget ONLY when clickActionValue !== 'none',
     * so pages that leave clickAction at its 'none' default attach no click listener
     * and preserve today's behavior exactly.
     *
     * Behaviors keyed on clickActionValue:
     *   - 'map-print-class' -> emit a bubbling `floorplan:icon-clicked` CustomEvent
     *     with detail { deviceId, deviceName, roomName, iconElement }.
     *   - 'select'          -> toggle the `selected` CSS class on the icon and emit a
     *     bubbling `floorplan:icon-selected` CustomEvent with detail
     *     { deviceId, selected, iconElement }.
     *   - 'none'            -> unreachable (no listener attached).
     *
     * A hold gesture sets the `holdFired` flag so the ensuing synthetic click is
     * suppressed: if holdFired is set we reset it and return without dispatching a
     * click action (Requirement 5.4, design.md Function 1 / Error Scenario 4).
     *
     * @param {Event} event - The DOM click event delegated from the device list.
     */
    handleIconClick(event) {
        // Suppress the click that immediately follows a fired hold gesture.
        if (this.holdFired) {
            this.holdFired = false
            return
        }

        // Resolve the icon element under the click. No-op if the click was not on a
        // clickable device icon (e.g. empty floor-plan space or a dropped icon).
        const iconElement = this.findClickableDeviceIcon(event.target)
        if (!iconElement) {
            return
        }

        const deviceId = iconElement.dataset.deviceId
        const device = this.findDeviceById(deviceId)

        switch (this.clickActionValue) {
            case 'map-print-class':
                this.emitIconClicked(iconElement, deviceId, device)
                break
            case 'select':
                this.emitIconSelected(iconElement, deviceId)
                break
            default:
                // 'none' or any unrecognized value: no-op (should be unreachable
                // because the listener is only attached when clickAction !== 'none').
                break
        }
    }

    /**
     * Emit a bubbling `floorplan:icon-clicked` CustomEvent for the map-print-class
     * click behavior. The Visual Print Mapper controller listens for this event to
     * apply the currently selected print class to the clicked device.
     *
     * @param {HTMLElement} iconElement - The clicked icon element
     * @param {string} deviceId - The clicked device's id
     * @param {Object} [device] - The device data object (from the store)
     */
    emitIconClicked(iconElement, deviceId, device) {
        const deviceName = device?.name || iconElement.dataset.deviceName || null
        const roomName = device?.locale?.room?.name || null

        iconElement.dispatchEvent(new CustomEvent('floorplan:icon-clicked', {
            detail: { deviceId, deviceName, roomName, iconElement },
            bubbles: true
        }))
    }

    /**
     * Toggle the `selected` CSS class on the clicked icon and emit a bubbling
     * `floorplan:icon-selected` CustomEvent reflecting the new selection state.
     *
     * @param {HTMLElement} iconElement - The clicked icon element
     * @param {string} deviceId - The clicked device's id
     */
    emitIconSelected(iconElement, deviceId) {
        const selected = iconElement.classList.toggle('selected')

        iconElement.dispatchEvent(new CustomEvent('floorplan:icon-selected', {
            detail: { deviceId, selected, iconElement },
            bubbles: true
        }))
    }

    /**
     * Dispatch double-click behavior based on the configured doubleClickAction.
     *
     * This is the single entry point for double-click (and mobile long-press-as-
     * double-click) behavior. It is keyed on doubleClickActionValue:
     *
     * - 'edit-device' -> navigate to the device/bundle edit page (current behavior)
     * - 'none'        -> no-op
     *
     * The device object is accepted for interface conformance (see design.md
     * Component 4 DoubleClickHandler); navigation is driven by the icon element's
     * data attributes exactly as before to preserve today's behavior precisely.
     *
     * @param {string} action - The configured double-click action
     * @param {HTMLElement} iconElement - The double-clicked/long-pressed icon element
     * @param {Object} [device] - The device data object (from the store)
     */
    handleDoubleClick(action, iconElement, device) {
        if (action === 'none') {
            return
        }

        if (action === 'edit-device') {
            this.openDeviceOrBundle(iconElement)
        }
    }

    /**
     * Handle double-clicks on device icons (desktop only)
     */
    handleDeviceIconDoubleClick(event) {
        // Skip if double-click is disabled
        if (this.doubleClickActionValue === 'none') {
            return
        }

        // Find the device icon element
        const iconElement = this.findClickableDeviceIcon(event.target)
        if (!iconElement) {
            return
        }

        // Prevent event from bubbling to floor plan click handler
        event.stopPropagation()

        const device = this.findDeviceById(iconElement.dataset.deviceId)
        this.handleDoubleClick(this.doubleClickActionValue, iconElement, device)
    }

    /**
     * Handle touch start for long press detection (mobile only)
     */
    handleDeviceIconTouchStart(event) {
        // Skip if double-click (long press on mobile) is disabled
        if (this.doubleClickActionValue === 'none') {
            return
        }

        const iconElement = this.findClickableDeviceIcon(event.target)
        if (!iconElement) {
            return
        }

        this.touchStartTime = Date.now()
        this.touchTimer = setTimeout(() => {
            // Long press detected (500ms) - open device/bundle
            const device = this.findDeviceById(iconElement.dataset.deviceId)
            this.handleDoubleClick(this.doubleClickActionValue, iconElement, device)
            this.touchTimer = null
        }, 500)
    }

    /**
     * Handle touch end to clear long press timer (mobile only)
     */
    handleDeviceIconTouchEnd(event) {
        if (this.touchTimer) {
            clearTimeout(this.touchTimer)
            this.touchTimer = null
        }
    }

    /**
     * Handle touch move to cancel long press if user is dragging (mobile only)
     */
    handleDeviceIconTouchMove(event) {
        if (this.touchTimer) {
            clearTimeout(this.touchTimer)
            this.touchTimer = null
        }
    }

    /**
     * Open device or bundle pages based on icon type
     */
    openDeviceOrBundle(iconElement) {
        // Check if this is a bundle or individual device
        const iconType = iconElement.dataset.iconType
        const deviceId = iconElement.dataset.deviceId

        if (!deviceId) {
            console.warn('floor_plan_icon_manager_controller::openDeviceOrBundle - no device ID found')
            return
        }

        if (iconType === 'bundle') {
            // For bundles, open the bundle update page
            this.openBundleUpdatePage(iconElement)
        } else {
            // For individual devices, open the device update page
            const updateUrl = `/devices/update?device=${ deviceId }`
            window.open(updateUrl, '_blank', 'noopener,noreferrer')
        }
    }

    /**
     * Open bundle update page for a bundle instance
     * 
     * Opens the bundle management page in a new tab, allowing users to
     * view and edit all devices and parts in the bundle instance.
     * 
     * @param {HTMLElement} bundleIconElement - The bundle icon element containing data attributes
     */
    openBundleUpdatePage(bundleIconElement) {
        const bundleInstanceId = bundleIconElement.dataset.bundleInstanceId
        const bundleId = bundleIconElement.dataset.bundleId

        if (!bundleInstanceId) {
            console.warn('floor_plan_icon_manager_controller::openBundleUpdatePage - no bundle instance ID found')
            return
        }

        if (!bundleId) {
            console.warn('floor_plan_icon_manager_controller::openBundleUpdatePage - no bundle ID found')
            return
        }

        // Open the bundle update page with both bundle and id parameters
        const updateUrl = `/bundles/update?id=${bundleInstanceId}&bundle=${bundleId}`
        window.open(updateUrl, '_blank', 'noopener,noreferrer')
    }

    /**
     * Find clickable device icon from event target
     */
    findClickableDeviceIcon(target) {
        let element = target
        const container = this.deviceListTarget

        while (element && element !== container) {
            // Look for device icons with device IDs (existing devices only, not dropped icons)
            if (element.dataset.deviceId && element.dataset.isDroppedIcon !== 'true') {
                return element
            }
            element = element.parentElement
        }
        return null
    }

    /**
     * Handle clicks on the floor plan to drop a device or bundle
     */
    handleFloorPlanClick(event) {
        // Check if dropping is enabled (prevents placing on update pages with existing coordinates)
        if (!this.checkDroppingEnabled()) {
            return
        }

        // Check if we have either a selected device type or bundle
        const hasDeviceType = this.deviceTypeStoreValue && this.deviceTypeStoreValue.id
        const hasBundle = this.bundleStoreValue && this.bundleStoreValue.id

        if (!hasDeviceType && !hasBundle) {
            return
        }

        // Get the floor plan image for boundary calculations
        const floorPlanImage = this.getFloorPlanImage()
        if (!floorPlanImage) {
            console.warn('floor_plan_icon_manager_controller::handleFloorPlanClick - no floor plan image found')
            return
        }

        // Calculate click position relative to the device list container (same as existing icons)
        const imageRect = floorPlanImage.getBoundingClientRect()
        const deviceListRect = this.deviceListTarget.getBoundingClientRect()

        // Calculate position relative to the device list container
        const x = event.clientX - deviceListRect.left
        const y = event.clientY - deviceListRect.top

        // Check if click is within the floor plan image bounds
        const imageLeft = imageRect.left - deviceListRect.left
        const imageTop = imageRect.top - deviceListRect.top
        const imageRight = imageLeft + imageRect.width
        const imageBottom = imageTop + imageRect.height

        if (x < imageLeft || x > imageRight || y < imageTop || y > imageBottom) {
            // Click was outside the floor plan image
            return
        }

        // Remove any existing dropped icon (this also clears coordinate fields)
        this.removeDroppedIcon()

        // Create and place the new icon
        // Use coordinates relative to device list container (same as existing icons)
        this.createDroppedIcon(x, y, x, y)
    }

    /**
     * Create a dropped device or bundle icon at the specified position
     */
    createDroppedIcon(dropZoneX, dropZoneY, imageX, imageY) {
        // Determine if we're dropping a device type or bundle
        const deviceType = this.deviceTypeStoreValue
        const bundle = this.bundleStoreValue

        const isBundle = bundle && bundle.id
        const selectedItem = isBundle ? bundle : deviceType

        if (!selectedItem) {
            console.warn('floor_plan_icon_manager_controller::createDroppedIcon - no device type or bundle selected')
            return
        }

        // Create the icon element
        const iconElement = document.createElement('div')
        iconElement.className = isBundle ? 'dropped-bundle-icon' : 'dropped-device-icon'
        iconElement.style.position = 'absolute'
        iconElement.style.left = `${ dropZoneX }px`
        iconElement.style.top = `${ dropZoneY }px`
        iconElement.style.width = '30px'
        iconElement.style.height = '30px'
        iconElement.style.display = 'block'
        iconElement.style.textAlign = 'center'
        iconElement.style.lineHeight = '30px'
        // A dropped icon is a draggable placement affordance only when drag is enabled.
        // When drag is disabled, never show a grab cursor.
        iconElement.style.cursor = this.dragValue ? 'grab' : 'default'
        iconElement.draggable = false

        // Add data attributes for dragging support
        iconElement.dataset.iconType = isBundle ? 'bundle' : 'device'
        iconElement.dataset.isDroppedIcon = 'true'

        // Add type-specific data attributes
        if (isBundle) {
            iconElement.dataset.bundleId = bundle.id
            iconElement.dataset.bundleName = bundle.name
        } else {
            iconElement.dataset.deviceTypeId = deviceType.id
            iconElement.dataset.deviceTypeName = deviceType.name
        }

        // Create badge span element
        const badgeSpan = document.createElement('span')
        badgeSpan.className = 'badge badge-pill badge-primary'
        // Use different colors for bundles vs devices
        badgeSpan.style.backgroundColor = '#007bff' // blue

        // Create icon element
        const iconI = document.createElement('i')
        // Ensure icon class uses 'fa' instead of 'fas' for proper Font Awesome rendering
        const iconClass = selectedItem.icon || this.defaultIconValue
        iconI.className = iconClass.replace(/\bfas\b/g, 'fa')
        iconI.style.fontSize = '16px'
        iconI.style.color = 'white'

        // Assemble the structure
        badgeSpan.appendChild(iconI)
        iconElement.appendChild(badgeSpan)

        // Add to the device list container (same as existing icons)
        this.deviceListTarget.appendChild(iconElement)

        // Store reference to the dropped icon
        this.droppedIcon = iconElement

        // Update the coordinate fields
        this.updateCoordinateFields(imageX, imageY)
    }

    /**
     * Remove the currently dropped icon
     */
    removeDroppedIcon() {
        if (this.droppedIcon) {
            this.droppedIcon.remove()
            this.droppedIcon = null
            this.clearCoordinateFields()
        }
    }

    // =============================================================================
    // DRAGGING (from floor_plan_draggable_icons_controller)
    // =============================================================================

    /**
     * Setup drag event listeners for all icons in the floor plan
     */
    setupDragListeners() {
        // Use event delegation on the device list container to handle dynamically added icons
        const container = this.hasDeviceListTarget ? this.deviceListTarget : this.floorPlanTarget
        if (!container) {
            return
        }

        // Mouse events for desktop
        container.addEventListener('mousedown', this.handleMouseDown.bind(this))
        document.addEventListener('mousemove', this.handleMouseMove.bind(this))
        document.addEventListener('mouseup', this.handleMouseUp.bind(this))

        // Touch events for mobile
        container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false })
        document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false })
        document.addEventListener('touchend', this.handleTouchEnd.bind(this))

        // Prevent default drag behavior on images
        container.addEventListener('dragstart', (e) => e.preventDefault())
    }

    /**
     * Handle mouse down on draggable icons (desktop)
     */
    handleMouseDown(event) {
        // Skip if this is a mobile device
        if (this.isMobile) {
            return
        }

        const iconElement = this.findDraggableIcon(event.target)
        if (!iconElement) {
            return
        }

        event.preventDefault()

        this.startDrag(iconElement, event.clientX, event.clientY)
    }

    /**
     * Handle touch start on draggable icons (mobile)
     */
    handleTouchStart(event) {
        // Skip if this is not a mobile device
        if (!this.isMobile) {
            return
        }

        const iconElement = this.findDraggableIcon(event.target)
        if (!iconElement) {
            return
        }

        // Don't prevent default here to allow long press detection for device icons
        // Only prevent default if this is not a clickable device icon
        if (!iconElement.dataset.deviceId || iconElement.dataset.isDroppedIcon === 'true') {
            event.preventDefault()
        }

        const touch = event.touches[0]
        this.startDrag(iconElement, touch.clientX, touch.clientY)
    }

    /**
     * Start dragging operation (common for mouse and touch)
     */
    startDrag(iconElement, clientX, clientY) {
        this.draggedElement = iconElement
        this.isDragging = true

        // Cancel any pending press-and-hold timer so a drag never fires the hold
        // action mid-gesture. This guards the mouse path on pages that enable BOTH
        // drag and a hold action: the touch path already cancels pending holds via
        // handleHoldCancel (touchmove), but the mouse path has no mousemove-based
        // hold cancel, so starting a drag must explicitly clear the hold timer.
        // (No-op on pages where holdAction is 'none' since holdTimer is never set.)
        if (this.holdTimer) {
            clearTimeout(this.holdTimer)
            this.holdTimer = null
            this.holdTarget = null
            this.holdStartPosition = null
        }

        // Calculate offset from pointer to element's top-left corner
        const rect = iconElement.getBoundingClientRect()

        this.dragOffset = {
            x: clientX - rect.left,
            y: clientY - rect.top
        }

        // Add dragging class for visual feedback
        iconElement.classList.add('dragging')
        iconElement.style.cursor = 'grabbing'
        iconElement.style.zIndex = '1000'

        // Clear any pending update timeout
        this.clearUpdateTimeout()
    }

    /**
     * Handle mouse move during drag (desktop)
     */
    handleMouseMove(event) {
        if (!this.isDragging || !this.draggedElement) {
            return
        }

        event.preventDefault()
        this.updateDragPosition(event.clientX, event.clientY)
    }

    /**
     * Handle touch move during drag (mobile)
     */
    handleTouchMove(event) {
        if (!this.isDragging || !this.draggedElement) {
            return
        }

        event.preventDefault()
        const touch = event.touches[0]
        this.updateDragPosition(touch.clientX, touch.clientY)
    }

    /**
     * Update drag position (common for mouse and touch)
     */
    updateDragPosition(clientX, clientY) {
        // Get the floor plan image as the boundary reference
        const floorPlanImage = this.getFloorPlanImage()
        if (!floorPlanImage) {
            // Fallback to parent container if no floor plan image found
            const container = this.draggedElement.parentElement
            const containerRect = container.getBoundingClientRect()

            let newX = clientX - containerRect.left - this.dragOffset.x
            let newY = clientY - containerRect.top - this.dragOffset.y

            const iconWidth = this.draggedElement.offsetWidth
            const iconHeight = this.draggedElement.offsetHeight

            newX = Math.max(0, Math.min(newX, containerRect.width - iconWidth))
            newY = Math.max(0, Math.min(newY, containerRect.height - iconHeight))

            this.draggedElement.style.left = `${ newX }px`
            this.draggedElement.style.top = `${ newY }px`
            return
        }

        // Use floor plan image boundaries
        const imageRect = floorPlanImage.getBoundingClientRect()
        const parentRect = this.draggedElement.parentElement.getBoundingClientRect()

        // Calculate new position relative to the parent container
        let newX = clientX - parentRect.left - this.dragOffset.x
        let newY = clientY - parentRect.top - this.dragOffset.y

        // Calculate the floor plan image boundaries relative to the parent container
        const imageLeft = imageRect.left - parentRect.left
        const imageTop = imageRect.top - parentRect.top
        const imageRight = imageLeft + imageRect.width
        const imageBottom = imageTop + imageRect.height

        // Constrain to floor plan image boundaries
        const iconWidth = this.draggedElement.offsetWidth
        const iconHeight = this.draggedElement.offsetHeight

        newX = Math.max(imageLeft, Math.min(newX, imageRight - iconWidth))
        newY = Math.max(imageTop, Math.min(newY, imageBottom - iconHeight))

        // Update position
        this.draggedElement.style.left = `${ newX }px`
        this.draggedElement.style.top = `${ newY }px`
    }

    /**
     * Handle mouse up to end drag (desktop)
     */
    handleMouseUp(event) {
        this.endDrag()
    }

    /**
     * Handle touch end to end drag (mobile)
     */
    handleTouchEnd(event) {
        this.endDrag()
    }

    /**
     * End dragging operation (common for mouse and touch)
     */
    endDrag() {
        if (!this.isDragging || !this.draggedElement) {
            return
        }

        this.isDragging = false

        // Remove dragging visual feedback
        this.draggedElement.classList.remove('dragging')

        // Restore appropriate cursor based on icon type and device.
        // endDrag() is only reachable when drag listeners are attached (dragValue === true),
        // so this preserves the prior grab/pointer restore behavior exactly.
        if (this.draggedElement.dataset.deviceId && this.draggedElement.dataset.isDroppedIcon !== 'true') {
            // Existing devices - cursor depends on mobile vs desktop
            this.draggedElement.style.cursor = this.isMobile ? 'grab' : 'pointer'
        } else {
            // Dropped icons are just draggable
            this.draggedElement.style.cursor = 'grab'
        }

        this.draggedElement.style.zIndex = ''

        // Check if this is a dropped icon (to-be-created device)
        const isDroppedIcon = this.draggedElement.dataset.isDroppedIcon === 'true'

        if (isDroppedIcon) {
            // For dropped icons, update the coordinate fields instead of making API calls
            this.updateDroppedIconCoordinates()
        } else {
            // Store the element data for the delayed update (existing devices)
            // Only existing devices/bundles should have device IDs
            const deviceId = this.draggedElement.dataset.deviceId || this.draggedElement.id
            if (deviceId) {
                this.pendingUpdate = {
                    deviceId: deviceId,
                    x: parseInt(this.draggedElement.style.left) || 0,
                    y: parseInt(this.draggedElement.style.top) || 0
                }

                // Start 500ms timer to persist position
                this.startUpdateTimer()
            }
        }

        this.draggedElement = null
    }

    /**
     * Find the draggable icon element from the event target
     */
    findDraggableIcon(target) {
        // Look for the icon wrapper element with data attributes or existing_device class
        let element = target
        const container = this.hasDeviceListTarget ? this.deviceListTarget : this.floorPlanTarget

        while (element && element !== container) {
            // Check for new icon format (from floor_plan_icons_controller)
            // Look for elements with device ID (existing devices/bundles)
            if (element.dataset.deviceId) {
                return element
            }
            // Check for dropped icons (to-be-created devices/bundles)
            if (element.dataset.iconType && element.dataset.isDroppedIcon === 'true') {
                return element
            }
            // Check for legacy existing_device class (from CoffeeScript)
            if (element.classList.contains('existing_device')) {
                return element
            }
            element = element.parentElement
        }
        return null
    }

    /**
     * Update coordinate fields when a dropped icon is moved
     */
    updateDroppedIconCoordinates() {
        if (!this.draggedElement) {
            return
        }

        const floorPlanImage = this.getFloorPlanImage()
        if (!floorPlanImage) {
            // Fallback: use position as-is if no floor plan image
            const x = parseInt(this.draggedElement.style.left) || 0
            const y = parseInt(this.draggedElement.style.top) || 0
            this.updateCoordinateFields(x, y)
            return
        }

        // Calculate position relative to floor plan image, not parent container
        const imageRect = floorPlanImage.getBoundingClientRect()
        const parentRect = this.draggedElement.parentElement.getBoundingClientRect()
        const elementX = parseInt(this.draggedElement.style.left) || 0
        const elementY = parseInt(this.draggedElement.style.top) || 0
        
        // Subtract image offset to get coordinates relative to image
        const imageLeft = imageRect.left - parentRect.left
        const imageTop = imageRect.top - parentRect.top
        
        const x = elementX - imageLeft
        const y = elementY - imageTop

        // Update coordinate fields
        this.updateCoordinateFields(x, y)
    }

    // =============================================================================
    // COORDINATE FIELD MANAGEMENT
    // =============================================================================

    /**
     * Update the coordinate fields with the new position
     */
    updateCoordinateFields(x, y) {
        // Use Stimulus targets to update coordinate fields
        if (this.hasCoordinateXTarget) {
            this.coordinateXTarget.value = Math.round(x)
        }

        if (this.hasCoordinateYTarget) {
            this.coordinateYTarget.value = Math.round(y)
        }
    }

    /**
     * Clear the coordinate fields
     */
    clearCoordinateFields() {
        // Use Stimulus targets to clear coordinate fields
        if (this.hasCoordinateXTarget) {
            this.coordinateXTarget.value = ''
        }

        if (this.hasCoordinateYTarget) {
            this.coordinateYTarget.value = ''
        }
    }

    // =============================================================================
    // API PERSISTENCE
    // =============================================================================

    /**
     * Find a device in the allDevicesStore by its ID
     */
    findDeviceById(deviceId) {
        // Resolve via the precomputed id -> device map instead of a linear scan on every
        // interaction. The map is (re)built at the top of each render pass; build it
        // lazily here if an interaction happens before the first render. Rebuild when the
        // requested id is absent, in case the store changed since the last render (a miss
        // is cheap and preserves the previous always-current-store semantics).
        if (!this._deviceById) {
            this._deviceById = new Map((this.allDevicesStoreValue || []).map(d => [d.id, d]))
        }

        let device = this._deviceById.get(deviceId)
        if (device === undefined) {
            this._deviceById = new Map((this.allDevicesStoreValue || []).map(d => [d.id, d]))
            device = this._deviceById.get(deviceId)
        }

        return device
    }

    /**
     * Start the 500ms timer before persisting position
     */
    startUpdateTimer() {
        this.clearUpdateTimeout()

        this.updateTimeout = setTimeout(() => {
            this.persistPosition()
        }, 500)
    }

    /**
     * Clear any pending update timeout
     */
    clearUpdateTimeout() {
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout)
            this.updateTimeout = null
        }
    }

    /**
     * Persist the new position to the server using PATCH /items/:id
     */
    async persistPosition() {
        // Defense-in-depth: position persistence must never occur when dragging is
        // disabled. Drag listeners are not attached when dragValue is false (so this
        // path is already unreachable), but guard here as well to guarantee no
        // position-update request is issued for a non-draggable page.
        if (!this.dragValue) {
            this.pendingUpdate = null
            return
        }

        if (!this.pendingUpdate) {
            console.warn('floor_plan_icon_manager_controller::persistPosition - no pending update data')
            return
        }

        const { deviceId, x, y } = this.pendingUpdate

        if (!deviceId) {
            console.warn('floor_plan_icon_manager_controller::persistPosition - no device ID found')
            return
        }

        try {
            // Find the device in the store to determine if it's a bundle
            const device = this.findDeviceById(deviceId)

            if (!device) {
                // Fallback to single item update if device not found in store
                await this.updateSingleItemPosition(deviceId, x, y)
                return
            }

            if (isBundle(device)) {
                // Handle bundle dragging - update all devices and parts in the bundle instance
                const bundleInstanceId = device.bundle_instance_id
                await this.updateBundlePosition(bundleInstanceId, x, y)
            } else {
                // Handle individual device/part dragging
                await this.updateSingleItemPosition(deviceId, x, y)
            }

        } catch (error) {
            console.error('floor_plan_icon_manager_controller::persistPosition - error:', error)
            sendFailureNotification('Failed to update position. Please try again.')
        } finally {
            // Clear the pending update after processing
            this.pendingUpdate = null
        }
    }

    /**
     * Update position for a single device or part
     */
    async updateSingleItemPosition(deviceId, x, y) {
        const updateData = {
            locale: {
                floorplan_x_coordinate: x,
                floorplan_y_coordinate: y
            }
        }


        // Build the URL
        const url = `/api/items/${ deviceId }`

        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrfToken()
            },
            body: JSON.stringify(updateData)
        })

        if (response.ok) {
            sendSuccessNotification('Position updated successfully')
        } else {
            console.error(`floor_plan_icon_manager_controller::updateSingleItemPosition - update failed: ${ response.status }`)
            sendFailureNotification('Unable to update device position. Please try again.')
            throw new Error(`HTTP ${ response.status }: ${ response.statusText }`)
        }
    }

    /**
     * Update position for all devices and parts in a bundle instance
     */
    async updateBundlePosition(bundleInstanceId, x, y) {
        // Get all devices from the store
        const allDevices = this.allDevicesStoreValue || []

        // Filter devices that belong to this bundle instance
        const bundleDevices = allDevices.filter(device =>
            device.bundle_instance_id === bundleInstanceId
        )

        if (bundleDevices.length === 0) {
            sendFailureNotification('No devices found in bundle. Please try again.')
            return
        }

        // Get the bundle_id from the first device in the bundle
        const bundleId = bundleDevices[0].bundle?.id || bundleDevices[0].bundle_id

        if (!bundleId) {
            console.error('floor_plan_icon_manager_controller::updateBundlePosition - no bundle_id found')
            sendFailureNotification('Unable to update bundle position. Please try again.')
            return
        }

        // Use the bundle instance update endpoint
        // This endpoint expects: PUT /api/items?bundle=<bundle_id>&bundle_instance=<bundle_instance_id>
        const updateData = {
            locale: {
                floorplan_x_coordinate: x,
                floorplan_y_coordinate: y
            }
        }

        const url = `/api/items?bundle=${bundleId}&bundle_instance=${bundleInstanceId}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify(updateData)
            })

            if (response.ok) {
                sendSuccessNotification(`Bundle position updated successfully (${bundleDevices.length} items)`)
            } else {
                const errorData = await response.json()
                console.error(`floor_plan_icon_manager_controller::updateBundlePosition - update failed: ${response.status}`, errorData)
                sendFailureNotification(errorData.message || 'Unable to update bundle position. Please try again.')
                throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`)
            }
        } catch (error) {
            console.error('floor_plan_icon_manager_controller::updateBundlePosition - error updating bundle:', error)
            if (!error.message.includes('HTTP')) {
                sendFailureNotification('Failed to update bundle position. Please try again.')
            }
            throw error
        }
    }

    // =============================================================================
    // UTILITY METHODS
    // =============================================================================

    /**
     * Find the floor plan image element for boundary calculations
     */
    getFloorPlanImage() {
        // Look for the floor plan image in the floor plan target
        if (this.hasFloorPlanTarget) {
            const image = this.floorPlanTarget.querySelector('img.floor-plan-image, img')
            if (image) {
                return image
            }
        }

        // Fallback: look for any floor plan image in the document
        return document.querySelector('.floor-plan-image, #floorplan img')
    }

    // =============================================================================
    // PUBLIC API METHODS
    // =============================================================================

    /**
     * Get all rendered device icons
     */
    getDeviceIcons() {
        if (!this.hasDeviceListTarget) {
            return []
        }
        return Array.from(this.deviceListTarget.querySelectorAll('[data-icon-type="device"]'))
    }

    /**
     * Get all rendered bundle icons
     */
    getBundleIcons() {
        if (!this.hasDeviceListTarget) {
            return []
        }
        return Array.from(this.deviceListTarget.querySelectorAll('[data-icon-type="bundle"]'))
    }

    /**
     * Get icon by device ID
     */
    getIconByDeviceId(deviceId) {
        if (!this.hasDeviceListTarget) {
            return null
        }
        return this.deviceListTarget.querySelector(`[data-device-id="${ deviceId }"]`)
    }

    /**
     * Get icon by bundle instance ID
     */
    getIconByBundleInstanceId(bundleInstanceId) {
        if (!this.hasDeviceListTarget) {
            return null
        }
        return this.deviceListTarget.querySelector(`[data-bundle-instance-id="${ bundleInstanceId }"]`)
    }

    /**
     * Get the current dropped icon coordinates (for external access)
     */
    getDroppedIconCoordinates() {
        if (!this.droppedIcon) {
            return null
        }

        const floorPlanImage = this.getFloorPlanImage()
        if (!floorPlanImage) {
            return null
        }

        const iconRect = this.droppedIcon.getBoundingClientRect()
        const imageRect = floorPlanImage.getBoundingClientRect()
        const dropZoneRect = this.dropZoneTarget.getBoundingClientRect()

        const imageLeft = imageRect.left - dropZoneRect.left
        const imageTop = imageRect.top - dropZoneRect.top

        const iconLeft = iconRect.left - dropZoneRect.left
        const iconTop = iconRect.top - dropZoneRect.top

        return {
            x: Math.round(iconLeft - imageLeft),
            y: Math.round(iconTop - imageTop)
        }
    }

    /**
     * Check if there is a dropped icon on the floor plan
     */
    hasDroppedIcon() {
        return this.droppedIcon !== null
    }

    /**
     * Manually clear the dropped icon (for external use)
     */
    clearDroppedIcon() {
        this.removeDroppedIcon()
    }

    /**
     * Manually refresh all icons
     */
    refresh() {
        this.renderIcons()
    }

    /**
     * Set the collection of device/workstation ids that currently have at least one
     * print mapping, then re-render so icon colors reflect mapping status.
     *
     * Consumed by the 'mapping-status' icon color mode (getIconColor): ids in this
     * set render green (#198754), everything else renders the fixed/red color. Also
     * drives the hide-mapped filter in renderIcons(). Called by the
     * print-mapper--mapping-status controller on area change and on live
     * mapping create/delete events, so colors and the hide filter update live.
     *
     * @param {Array<string>} idsArray - Device/workstation ids that have a mapping
     */
    setMappedDeviceIds(idsArray) {
        this.mappedDeviceIds = new Set(Array.isArray(idsArray) ? idsArray : [])
        // Coalesce with any store-driven renders in the same burst. On the Visual Print
        // Mapper this recolor is triggered right after a mapping is created/deleted and
        // used to race the device/area store re-renders, which is why the green color and
        // connection line sometimes only appeared after a manual page refresh. Batching
        // them into one render pass makes the live update deterministic.
        this.scheduleRender()
    }

    /**
     * Toggle whether devices that have print mappings are hidden from the floor plan.
     *
     * When true, renderIcons() drops any device whose id is in mappedDeviceIds so only
     * unmapped devices remain. When false (default), all devices render. Re-renders on
     * every call so the toggle takes effect immediately.
     *
     * @param {boolean} bool - Whether to hide mapped devices
     */
    setHideMappedDevices(bool) {
        this.hideMappedDevices = !!bool
        this.scheduleRender()

        // Notify listeners (e.g. print-mapper--connection-lines) that the set of
        // visible mapped devices changed, so they can hide/show the connection lines
        // that terminate on now-hidden device icons. Bubbles to the shared page root.
        this.element.dispatchEvent(new CustomEvent('floorplan:mapped-visibility-changed', {
            bubbles: true,
            detail: {
                hidden: this.hideMappedDevices,
                mappedDeviceIds: Array.from(this.mappedDeviceIds || [])
            }
        }))
    }

    /**
     * Hide existing device icons (but keep dropped icons)
     */
    hideExistingDeviceIcons() {
        this.showExistingDevices = false
        this.renderIcons()
    }

    /**
     * Show existing device icons
     */
    showExistingDeviceIcons() {
        this.showExistingDevices = true
        this.renderIcons()
    }

    /**
     * Hide all icons (including dropped icons)
     */
    hideAllIcons() {
        this.clearAllIcons()
    }

    /**
     * Show all icons
     */
    showAllIcons() {
        this.showExistingDevices = true
        this.renderIcons()
    }

    /**
     * Enable dragging for all icons
     */
    enableDragging() {
        const container = this.hasDeviceListTarget ? this.deviceListTarget : this.floorPlanTarget
        if (!container) {
            return
        }

        // Enable dragging for existing devices/bundles with device IDs
        const existingIcons = container.querySelectorAll('[data-device-id]')
        existingIcons.forEach(icon => {
            icon.style.cursor = 'grab'
            icon.draggable = false // Disable native drag to use custom implementation
        })

        // Enable dragging for dropped icons (to-be-created devices/bundles)
        const droppedIcons = container.querySelectorAll('[data-icon-type][data-is-dropped-icon="true"]')
        droppedIcons.forEach(icon => {
            icon.style.cursor = 'grab'
            icon.draggable = false // Disable native drag to use custom implementation
        })

        // Enable dragging for legacy existing_device elements
        const legacyIcons = container.querySelectorAll('.existing_device')
        legacyIcons.forEach(icon => {
            icon.style.cursor = 'grab'
            icon.draggable = false // Disable native drag to use custom implementation
        })
    }

    /**
     * Disable dragging for all icons
     */
    disableDragging() {
        const container = this.hasDeviceListTarget ? this.deviceListTarget : this.floorPlanTarget
        if (!container) {
            return
        }

        // Disable dragging for existing devices/bundles with device IDs
        const existingIcons = container.querySelectorAll('[data-device-id]')
        existingIcons.forEach(icon => {
            icon.style.cursor = 'default'
        })

        // Disable dragging for dropped icons (to-be-created devices/bundles)
        const droppedIcons = container.querySelectorAll('[data-icon-type][data-is-dropped-icon="true"]')
        droppedIcons.forEach(icon => {
            icon.style.cursor = 'default'
        })

        // Enable dragging for legacy existing_device elements
        const legacyIcons = container.querySelectorAll('.existing_device')
        legacyIcons.forEach(icon => {
            icon.style.cursor = 'default'
        })
    }

    /**
     * Toggle visibility of existing device icons while preserving dropped icons
     * This is used by the "Hide Devices" button on the walkthrough page
     */
    toggleDeviceVisibility() {
        // Toggle the flag and re-render
        this.showExistingDevices = !this.showExistingDevices

        // Update button text based on new state
        if (this.showExistingDevices) {
            this.updateToggleButtonText('Hide Devices')
        } else {
            this.updateToggleButtonText('Show Devices')
        }

        // Re-render icons with new visibility state
        this.renderIcons()
    }

    /**
     * Update the toggle button text
     */
    updateToggleButtonText(text) {
        const toggleButton = document.getElementById('device-toggle')
        if (toggleButton) {
            toggleButton.textContent = text
            toggleButton.value = text
        }
    }

    /**
     * Update the toggle button enabled/disabled state based on area selection
     */
    updateToggleButtonState() {
        const toggleButton = document.getElementById('device-toggle')
        if (toggleButton) {
            const hasArea = this.areaStoreValue && this.areaStoreValue.id
            toggleButton.disabled = !hasArea

            // Reset button text when area changes
            if (hasArea) {
                this.updateToggleButtonText(this.showExistingDevices ? 'Hide Devices' : 'Show Devices')
            }
        }
    }
}